import React, { useEffect, useMemo, useRef, useState } from 'react';
import _ from 'lodash';
import moment from 'moment';
import { useNavigate, Link, Navigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { Helmet } from 'react-helmet';
import {
  Button,
  Grid,
  CircularProgress,
  Divider,
  Typography,
  TextField,
  Box,
  InputAdornment,
  IconButton,
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Select,
  MenuItem,
  ListSubheader,
  Checkbox,
  Alert,
  FormControl,
  FormControlLabel,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from '@mui/material';

import { useQuery, useLazyQuery, useMutation } from '@apollo/client';
import i18next from 'i18next';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import ThumbDownIcon from '@mui/icons-material/ThumbDown';
import UploadIcon from '@mui/icons-material/Upload';
import CancelIcon from '@mui/icons-material/Cancel';
import SaveIcon from '@mui/icons-material/Save';
import DownloadIcon from '@mui/icons-material/Download';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ClearIcon from '@mui/icons-material/Clear';
import ErrorIcon from '@mui/icons-material/ErrorOutline';
import DeleteIcon from '@mui/icons-material/Delete';
import ReceiptIcon from '@mui/icons-material/Receipt';
import { dispatchException, dispatchMessage } from 'helper/snackbar';

import { CustomTabsControlled } from 'components/Tabs';
import SimpleTable from 'components/table/SimpleTable';
import BookingTasksList from 'components/BookingTasksList';

import ConfirmationButton from 'components/dialogs/ConfirmationButton';
import { UnsavedChangesPrompt } from 'components/form/UnsavedChangesPrompt';

import {
  EVICT_INVOICE_QUERIES,
  INVOICE_APPROVE_PREMIUM_MUTATION,
  REFETCH_INVOICE_QUERIES,
  LIST_INVOICE_COSTCENTRES_QUERY,
  INVOICE_GENERATE_MUTATION,
  INVOICE_VIEW_OFFERVERSIONMEDIA_QUERY,
} from './gql';

import {
  ApprovePremiumInvoiceMutation,
  ApprovePremiumInvoiceMutationVariables,
  EAMSplitInvoicingRule,
  EContentProductDetailPosition,
  ECountryCode,
  EOfferCommissionBase,
  EOfferSource,
  EOfferVersionContentBlockLineItemSource,
  EOfferVersionContentBlockType,
  EOfferVersionHistoryStatus,
  EOfferVersionStatus,
  EOfferVersionType,
  EProductLookupCode,
  GenerateClientInvoicesMutation,
  ListInvoiceAccountingAreasQuery,
  ListInvoiceCostCentresQuery,
  ViewInvoiceQuery,
  ViewWlOfferQuery,
  ViewWlOfferVersionQuery,
  WLInvoiceAttendanceInput,
  WLOfferListOutput,
  WLOfferListViewFragment,
  WLOfferVersionContentBlockLineItemInput,
  WLOfferVersionDiffOutputFragment,
  WLOfferVersionListOutput,
  WLOfferVersionListViewFragment,
} from '__generated__/graphql';

import { FormatDate, FormatDateTime, formatDate } from 'components/DateTime';

import { NO_PRICE } from '../../semshared/pricelist/quickprice';

import { RedirectError } from 'pages/error';
import { formatDocumentTitle } from 'helper/usedocumenttitle';
import yup from 'validation';
import { Controller, FormProvider, useFieldArray, useForm, useFormContext, useWatch } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { formatPrice } from 'components/Price';
import { PremiumInvoiceCancellationWaiverSection } from './PremiumInvoiceCancellationWaiverSection';
import type { PremiumInvoiceCancellationWaiverPreview } from './PremiumInvoiceCancellationWaiverSection';
import { calculateTotals, getFullEventDayRange } from 'semshared/offer/offer';
import {
  CancellationWaiveMode,
  inferCancellationWaiveMode,
  resolveCancellationAmountToInvoice,
} from 'semshared/offer/cancellationWaiver';
import { FormInputText } from 'components/form/FormInputText';
import { FormInputNumber } from 'components/form/FormInputNumber';
import { FormInputCurrency } from 'components/form/FormInputCurrency';
import { FormInputCheckbox } from 'components/form/FormInputCheckbox';
import { FormInputPercentage } from 'components/form/FormInputPercentage';
import { FormInputCountryDropdown, FormInputDropdown } from 'components/form/FormInputDropdown';
import FileUploadButton from 'components/FileUploadButton';
import { ProductTextSelector, ProductTextSelectorType } from 'semshared/pricelist/producttextselector';
import InformationDialog from 'components/dialogs/InformationDialog';
import { exportBase64ToFile, getFullBackendUrl } from 'helper/download';
import { formatPercentage } from 'components/Percentage';
import {
  EAM_EventProductLookupCodes,
  isValidAttendanceLookupCode,
  mapAttendanceDirectPay,
  mapAttendanceRegistered,
  mapAttendanceShow,
  mapLineItems2GuestCount,
  mapShowAttendance,
} from 'semshared/offer/invoice';
import { userSelector } from 'helper/security';
import ConfirmationDialog from 'components/dialogs/ConfirmationDialog';
import SimpleDialog from 'components/dialogs/Dialog';
import OfferHistoryTable from 'components/OfferHistoryTable';
import ContactPersonTabsWrapper from 'components/ContactPersonTabsWrapper';
import { formatOfferDay } from 'semshared/utils/format';
import { QuickPriceCalculatorLister, QuickPriceCalculatorTypeLister } from 'semshared/pricelist/quickprice_lister';
import { COMMISSIONABLE_LOOKUP_CODES, getDefaultCommissionRate } from 'semshared/offer/commission';
import { roundPrice } from 'semshared/utils/round';
import InvoiceCancellationFormHotel from './invoice_cancellation';
import { quickPriceSkuDisplayName } from 'helper/quickPriceSkuDisplayName';

interface InvoicePremiumFormProps {
  offer: NonNullable<ViewInvoiceQuery['viewWlOffer']>;
  version: NonNullable<ViewInvoiceQuery['viewWlOfferInvoiceVersion']>;
  downloadVersion?: ViewInvoiceQuery['viewWlOfferInvoiceDownloadVersion'];
  invoices: NonNullable<ViewInvoiceQuery['viewWlOfferGeneratedInvoices']>;
  event: ViewInvoiceQuery['viewWlOfferInvoiceEvent'];
  costCentres: NonNullable<ListInvoiceCostCentresQuery['listInvoiceCostCentres']>;
  accountingAreas: NonNullable<ListInvoiceAccountingAreasQuery['listInvoiceAccountingAreas']>;
  invoiceCostCentres: NonNullable<ListInvoiceCostCentresQuery['listInvoiceCostCentres']>;
  invoiceAccountingAreas: NonNullable<ListInvoiceAccountingAreasQuery['listInvoiceAccountingAreas']>;
  quickPriceCalculator: QuickPriceCalculatorTypeLister;
  productTexts: ProductTextSelectorType;
}

function InvoiceReadonlyBookedServices({
  version,
  quickPriceCalculator,
}: {
  version: NonNullable<ViewInvoiceQuery['viewWlOfferInvoiceVersion']>;
  quickPriceCalculator: QuickPriceCalculatorTypeLister;
}) {
  const dayBlocks = version.contentBlocks
    .filter(cb => cb.type === EOfferVersionContentBlockType.LINE_ITEMS && cb.lineItems.length > 0)
    .slice()
    .sort((a, b) => a.lineItems[0].day - b.lineItems[0].day);

  if (dayBlocks.length === 0) return null;

  return (
    <Grid item xs={12}>
      <Grid container spacing={2}>
        <Grid item xs={12} sx={{ pb: 0.5 }}>
          <Typography variant="subtitle1" fontWeight={700}>
            {i18next.t('invoice-booked-services-readonly-title')}
          </Typography>
        </Grid>
        {dayBlocks.map((cb, cbIndex) => (
          <Grid item xs={12} key={`readonly_day_${cbIndex}`} sx={{ pt: '6px !important' }}>
            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography>
                  {formatOfferDay(cb.lineItems[0].day)} - {formatDate(moment.utc(version.startDate, 'YYYY-MM-DD').add(cb.lineItems[0].day, 'days').toDate())} ({cb.lineItems.length})
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ pt: 2 }}>
                <Grid container rowSpacing={2} columnSpacing={1.5}>
                  {cb.lineItems.map((li, liIndex) => (
                    <React.Fragment key={`readonly_li_${cbIndex}_${liIndex}`}>
                      <Grid item sm={1}>
                        <TextField label={i18next.t('invoice-lineitem-count')} value={li.count} disabled fullWidth />
                      </Grid>
                      <Grid item sm={7}>
                        <Accordion>
                          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Typography>
                              {li.header} ({li.sku})
                            </Typography>
                          </AccordionSummary>
                          <AccordionDetails sx={{ pt: 2 }}>
                            <Grid container rowSpacing={2} columnSpacing={1.5}>
                              <Grid item xs={12} sm={6}>
                                <TextField
                                  label={i18next.t('invoice-lineitem-header')}
                                  value={li.header}
                                  disabled
                                  fullWidth
                                />
                              </Grid>
                              <Grid item xs={12} sm={6}>
                                <TextField
                                  label={i18next.t('invoice-lineitem-details')}
                                  value={li.details}
                                  disabled
                                  fullWidth
                                  multiline
                                  rows={3}
                                />
                              </Grid>
                            </Grid>
                          </AccordionDetails>
                        </Accordion>
                      </Grid>
                      <Grid item sm={2}>
                        {li.components.length > 1 && (
                          <Grid container rowSpacing={2.5}>
                            {li.components.map((c, cIndex) => (
                              <Grid item sm={12} key={cIndex} sx={{ mb: 0.5 }}>
                                <TextField
                                  label={`${quickPriceCalculator.getTaxRateForType(c.type.id)?.name || c.type.name} ${i18next.t(`invoice-lineitem-suffix-${version.priceList.isPricesNet ? 'net' : 'gross'}`)}`}
                                  value={formatPrice(c.price, version.priceList.currency)}
                                  disabled
                                  fullWidth
                                />
                              </Grid>
                            ))}
                          </Grid>
                        )}
                        {li.components.length <= 1 && (
                          <TextField
                            label={version.priceList.isPricesNet ? i18next.t('invoice-lineitem-price-net') : i18next.t('invoice-lineitem-price-gross')}
                            value={formatPrice(li.priceItem, version.priceList.currency)}
                            disabled
                            fullWidth
                          />
                        )}
                      </Grid>
                      <Grid item sm={1}>
                        <TextField
                          label={i18next.t('invoice-lineitem-net')}
                          value={formatPrice(li.priceNet, version.priceList.currency)}
                          disabled
                          fullWidth
                        />
                      </Grid>
                      <Grid item sm={1}>
                        <TextField
                          label={i18next.t('invoice-lineitem-gross')}
                          value={formatPrice(li.priceGross, version.priceList.currency)}
                          disabled
                          fullWidth
                        />
                      </Grid>
                    </React.Fragment>
                  ))}
                </Grid>
              </AccordionDetails>
            </Accordion>
          </Grid>
        ))}
        <Grid item xs={12} sx={{ pt: '6px !important' }}>
          <hr />
        </Grid>
      </Grid>
    </Grid>
  );
}

const taxesSchema = yup.object().shape({
  taxTypeId: yup.number().nullable(),
  taxRateId: yup.number().required(),
  name: yup.string().required(),
  rate: yup.number().required(),
  price: yup
    .number()
    .required()
    .notOneOf([NO_PRICE], `${i18next.t('invoice-err-noprice')}`),
});

const attendanceSchema = yup.object().shape({
  profileId: yup.number().required(),
  profileName: yup.string().required(),
  day: yup.number().required(),
  directPay: yup.boolean().required(),
  show: yup.boolean().required(),
});

/** One entry per registered (attending) participant-day for the NO SHOWS tab. */
const noShowSchema = yup.object().shape({
  profileId: yup.number().required(),
  day: yup.number().required(),
  noShow: yup.boolean().required(),
});

const makeLineItemPriceSchema = (allowNegative: boolean) => {
  let schema = yup
    .number()
    .required()
    .notOneOf([NO_PRICE], `${i18next.t('invoice-err-noprice')}`);
  if (!allowNegative) {
    schema = schema.min(0);
  }
  return schema;
};

const makeComponentsSchema = (allowNegative: boolean) =>
  yup.object().shape({
    taxTypeId: yup.number().required(),
    name: yup.string().required(),
    price: makeLineItemPriceSchema(allowNegative).label(i18next.t('invoice-lineitem-priceitem')),
  });

const componentsSchema = makeComponentsSchema(true);

const baseLineItemSchema = yup.object().shape({
  lineItemId: yup.number().required(),
  day: yup.number().required(),
  count: yup.number().required().label(i18next.t('invoice-lineitem-count')),
  sku: yup.string().nullable(),
  header: yup.string().required().label(i18next.t('invoice-lineitem-header')),
  details: yup.string().nullable(),
  priceItem: makeLineItemPriceSchema(true),
  priceGross: makeLineItemPriceSchema(true),
  priceNet: makeLineItemPriceSchema(true),
  components: yup.array().required().of(componentsSchema),
  taxes: yup.array().required().of(taxesSchema),
  media: yup.object().nullable().shape({
    id: yup.number().required(),
    name: yup.string().required(),
    mimeType: yup.string().required(),
    base64: yup.string().nullable(),
    size: yup.number().required(),
  }),
  source: yup.mixed<EOfferVersionContentBlockLineItemSource>().oneOf(Object.values(EOfferVersionContentBlockLineItemSource)).required(),
  hasAttendances: yup.boolean().required(),
  attendances: yup.array().required().of(attendanceSchema),
});

const lineItemInvoiceSchema = yup.object().shape({
  commission: yup.boolean().required(),
  costCentre: yup.string().nullable(),
  accountingArea: yup.string().nullable(),
  invoicingRule: yup.mixed<EAMSplitInvoicingRule>().oneOf(Object.values(EAMSplitInvoicingRule)).nullable(),
});

const lineItemSchema = baseLineItemSchema.shape({
  isChanged: yup.boolean().required(),
  invoice: lineItemInvoiceSchema.nullable(),
});

const daySchema = yup.object().shape({
  day: yup.number().required(),
  lineItems: yup.array().required().of(lineItemSchema),
  accepted: yup.boolean().required(),
  expanded: yup.boolean().required(),
});

const extraLineItemSchema = baseLineItemSchema.shape({
  isDeleted: yup.boolean().required(),
  invoice: lineItemInvoiceSchema.nullable(),
});

/** Hotel Extrabelege must stay non-negative; SEM/root may enter deductions. */
const extraLineItemSchemaNonNegative = extraLineItemSchema.shape({
  priceItem: makeLineItemPriceSchema(false),
  components: yup.array().required().of(makeComponentsSchema(false)),
});

const cancellationlineItemSchema = baseLineItemSchema.shape({
  isChanged: yup.boolean().required(),
});

const cancellationSchema = yup.object().shape({
  daysToEvent: yup.number(),
  rate: yup.number(),
  lineItems: yup.array().of(cancellationlineItemSchema),
});

const addressSchema = yup.object().shape({
  businessName: yup.string().required().label(i18next.t('invoice-sender-name')),
  businessAddress1: yup.string().required().label(i18next.t('invoice-sender-address1')),
  businessAddress2: yup.string().nullable(),
  businessAddress3: yup.string().nullable(),
  businessAddress4: yup.string().nullable(),
  businessZip: yup.string().nullable().label(i18next.t('invoice-sender-zip')),
  businessCity: yup.string().nullable().label(i18next.t('invoice-sender-city')),
  businessCountry: yup.mixed<ECountryCode>().oneOf(Object.values(ECountryCode)).required().label(i18next.t('invoice-sender-country')),
  billingUid: yup.string().required().label(i18next.t('invoice-sender-billing-uid')),
  businessEmail: yup.string().nullable(),
  businessPhone: yup.string().nullable(),
  contactName: yup.string().nullable(),
  comment: yup.string().nullable(),
  accepted: yup.boolean().required(),
});

const validationSchema = yup.object().shape({
  days: yup.array().required().of(daySchema),
  noShows: yup.array().required().of(noShowSchema),
  extraLineItems: yup.array().required().of(extraLineItemSchema),
  cancellations: yup.array().required().of(cancellationSchema),
  agreedCancellationWaiveNet: yup.number().nullable(),
  manualCancellationWaiveNet: yup.number().nullable(),
  /** Premium SM-2791 path: waive radios (Amount to invoice in manualCancellationWaiveNet); hotel + SEM. */
  cancellationWaiveMode: yup.mixed<CancellationWaiveMode>().oneOf(['WAIVE', 'PARTIAL', 'CHARGE']).nullable(),
  cancellationChargedAmount: yup
    .number()
    .nullable()
    .transform(value => (Number.isNaN(value) ? null : value))
    .when('cancellationWaiveMode', {
      is: 'PARTIAL',
      then: schema => schema.required().min(0, `${i18next.t('invoice-cancellation-manualwaive-min-error')}`),
    }),
  totalPriceNet: yup
    .number()
    .required()
    .notOneOf([NO_PRICE], `${i18next.t('invoice-err-noprice')}`),
  totalPriceGross: yup
    .number()
    .required()
    .notOneOf([NO_PRICE], `${i18next.t('invoice-err-noprice')}`),
  totalTaxes: yup.array().required().of(taxesSchema),
  sender: addressSchema.required(),
  commissionBase: yup.mixed<EOfferCommissionBase>().oneOf(Object.values(EOfferCommissionBase)).nullable(),
  commissionBaseAmount: yup
    .number()
    .nullable()
    .transform(value => (Number.isNaN(value) ? null : value)),
  commissionPercentage: yup
    .number()
    .nullable()
    .transform(value => (Number.isNaN(value) ? null : value)),
  commissionAmountNet: yup
    .number()
    .nullable()
    .transform(value => (Number.isNaN(value) ? null : value)),
  commissionAmountGross: yup
    .number()
    .nullable()
    .transform(value => (Number.isNaN(value) ? null : value)),
  commissionAccepted: yup.boolean().required(),
});

/** Soft-deleted extras stay in the form array but must not block approval. */
const createValidationSchemaResolved = (allowNegativeExtraPrices: boolean, requireSemCommission: boolean) => {
  const activeExtraSchema = allowNegativeExtraPrices ? extraLineItemSchema : extraLineItemSchemaNonNegative;
  return validationSchema.shape({
    extraLineItems: yup.array().required().of(
      yup.lazy((value: { isDeleted?: boolean } | undefined) =>
        value?.isDeleted
          ? yup.object({
              isDeleted: yup.boolean().required(),
            })
          : activeExtraSchema,
      ),
    ),
    ...(requireSemCommission
      ? {
          commissionBase: yup
            .mixed<EOfferCommissionBase>()
            .oneOf(Object.values(EOfferCommissionBase))
            .required()
            .label(i18next.t('invoice-commission-base')),
          commissionBaseAmount: yup
            .number()
            .required()
            .label(i18next.t('invoice-commission-base-amount'))
            .transform(value => (Number.isNaN(value) ? null : value)),
          commissionPercentage: yup
            .number()
            .required()
            .label(i18next.t('invoice-commission-percentage'))
            .transform(value => (Number.isNaN(value) ? null : value)),
          commissionAmountNet: yup
            .number()
            .required()
            .transform(value => (Number.isNaN(value) ? null : value)),
          commissionAmountGross: yup
            .number()
            .required()
            .transform(value => (Number.isNaN(value) ? null : value)),
        }
      : {}),
  });
};

export type InvoiceFormType = yup.InferType<typeof validationSchema>;
export type DayFormType = yup.InferType<typeof daySchema>;
export type LineItemFormType = yup.InferType<typeof lineItemSchema>;
export type ExtraLineItemFormType = yup.InferType<typeof extraLineItemSchema>;
export type ExtraMediaFormType = NonNullable<ExtraLineItemFormType['media']>;
export type AttendanceFormType = yup.InferType<typeof attendanceSchema>;
export type NoShowFormType = yup.InferType<typeof noShowSchema>;
export type TaxesType = yup.InferType<typeof taxesSchema>;

interface InvoiceFormDayProps extends InvoicePremiumFormProps {
  dayIndex: number;
  disabled: boolean;
  canEditItems: boolean;
  canAccept: boolean;
  isPricesNet: boolean;
  onEditInvoice?: () => void;
  setHasAllAccepted: (a: boolean) => void;
  lineItemCountChanged: (dayIndex: number, lineItemIndex: number) => void;
  lineItemAttendanceChanged: (dayIndex: number, lineItemIndex: number, profileId: number, show: boolean) => void;
}
interface InvoiceFormLineItemProps extends InvoiceFormDayProps {
  lineItemIndex: number;
}
interface InvoiceFormLineItemAttendanceProps extends InvoiceFormLineItemProps {
  attendanceIndex: number;
}
interface InvoiceFormLineItemComponentProps extends InvoiceFormLineItemProps {
  lineItemComponentIndex: number;
}

function InvoiceFormDay(props: InvoiceFormDayProps) {
  const { dayIndex, version, disabled, canAccept, onEditInvoice } = props;
  const { control, watch, setValue } = useFormContext<InvoiceFormType>();

  const isInitialRender = useRef(true);

  const dayWatch = watch(`days.${dayIndex}.day`);
  const dayDate = moment.utc(version.startDate, 'YYYY-MM-DD').add(dayWatch, 'days').toDate();

  const acceptedWatch = watch(`days.${dayIndex}.accepted`);

  useEffect(() => {
    if (isInitialRender.current) {
      isInitialRender.current = false;
      return;
    }

    if (!disabled && canAccept) {
      const daysWatch = watch('days');
      const na = daysWatch.find(d => !d.accepted);
      props.setHasAllAccepted(!na);
    }

    if (!disabled && canAccept && acceptedWatch) {
      setValue(`days.${dayIndex}.expanded`, false, { shouldDirty: true });
      if (dayIndex < watch('days').length - 1) {
        setValue(`days.${dayIndex + 1}.expanded`, true, { shouldDirty: true });
      }
    }
  }, [acceptedWatch]);

  const { fields: lineItemsFields } = useFieldArray({
    control,
    name: `days.${dayIndex}.lineItems`,
  });

  return (
    <Accordion expanded={watch(`days.${dayIndex}.expanded`)} onChange={(ev, expanded) => setValue(`days.${dayIndex}.expanded`, expanded)}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography>
          {formatOfferDay(dayWatch!)} - {formatDate(dayDate)} ({lineItemsFields.length})
        </Typography>
      </AccordionSummary>
      <AccordionDetails>
        <Grid container spacing={1}>
          {lineItemsFields.map((li, index) => (
            <InvoiceFormLineItem key={index} lineItemIndex={index} {...props} onEditInvoice={li.hasAttendances ? undefined : onEditInvoice} />
          ))}
        </Grid>
        {!disabled && canAccept && (
          <Grid container>
            <Grid item xs={12} justifyItems={'end'}>
              <FormInputCheckbox name={`days.${dayIndex}.accepted`} label={i18next.t('invoice-day-accepted')} control={control} />
            </Grid>
          </Grid>
        )}
      </AccordionDetails>
    </Accordion>
  );
}

function InvoiceFormLineItem(props: InvoiceFormLineItemProps) {
  const { dayIndex, lineItemIndex, version, disabled, canEditItems, isPricesNet, onEditInvoice } = props;
  const {
    control,
    watch,
    setValue,
    formState: { defaultValues },
  } = useFormContext<InvoiceFormType>();

  const isInitialRender = useRef({ count: true, components: true, header: true });

  const countWatch = watch(`days.${dayIndex}.lineItems.${lineItemIndex}.count`);
  const skuWatch = watch(`days.${dayIndex}.lineItems.${lineItemIndex}.sku`);
  const headerWatch = watch(`days.${dayIndex}.lineItems.${lineItemIndex}.header`);
  const detailsWatch = watch(`days.${dayIndex}.lineItems.${lineItemIndex}.details`);
  const hasAttendancesWatch = watch(`days.${dayIndex}.lineItems.${lineItemIndex}.hasAttendances`);
  const priceWatch = watch(`days.${dayIndex}.lineItems.${lineItemIndex}.priceItem`);
  const componentsWatch = watch(`days.${dayIndex}.lineItems.${lineItemIndex}.components`);

  const { fields: attendancesFields } = useFieldArray({
    control,
    name: `days.${dayIndex}.lineItems.${lineItemIndex}.attendances`,
  });
  const canEditAttendances = hasAttendancesWatch && attendancesFields.length > 0;

  useEffect(() => {
    if (isInitialRender.current.count) {
      isInitialRender.current.count = false;
      return;
    }

    if (disabled || canEditAttendances) return;
    if (!defaultValues || countWatch !== defaultValues.days![dayIndex]!.lineItems![lineItemIndex]!.count) {
      props.lineItemCountChanged(dayIndex, lineItemIndex);
    }
  }, [countWatch]);

  useEffect(() => {
    if (isInitialRender.current.components) {
      isInitialRender.current.components = false;
      return;
    }

    if (disabled || !canEditItems) return;
    if (componentsWatch.length > 1) return;
    if (!defaultValues || priceWatch !== defaultValues.days![dayIndex]!.lineItems![lineItemIndex]!.priceItem) {
      props.lineItemCountChanged(dayIndex, lineItemIndex);
    }
  }, [componentsWatch, priceWatch]);

  useEffect(() => {
    if (isInitialRender.current.header) {
      isInitialRender.current.header = false;
      return;
    }

    if (disabled || !canEditItems) return;
    if (
      !defaultValues ||
      headerWatch !== defaultValues.days![dayIndex]!.lineItems![lineItemIndex]!.header ||
      detailsWatch !== defaultValues.days![dayIndex]!.lineItems![lineItemIndex]!.details
    ) {
      setValue(`days.${dayIndex}.lineItems.${lineItemIndex}.isChanged`, true);
    }
  }, [headerWatch, detailsWatch]);

  const { fields: componentsFields } = useFieldArray({
    control,
    name: `days.${dayIndex}.lineItems.${lineItemIndex}.components`,
  });

  return (
    <>
      <Grid item sm={1}>
        <FormInputNumber
          label={i18next.t('invoice-lineitem-count')}
          name={`days.${dayIndex}.lineItems.${lineItemIndex}.count`}
          control={control}
          disabled={disabled || canEditAttendances}
        />
      </Grid>
      <Grid item sm={6}>
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography>
              {watch(`days.${dayIndex}.lineItems.${lineItemIndex}.isChanged`) ? '*' : ''} {headerWatch} ({skuWatch})
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={1}>
              {!disabled && canEditItems && (
                <>
                  <Grid item xs={12} sm={6}>
                    <FormInputText
                      label={i18next.t('invoice-lineitem-header')}
                      name={`days.${dayIndex}.lineItems.${lineItemIndex}.header`}
                      control={control}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormInputText
                      label={i18next.t('invoice-lineitem-details')}
                      name={`days.${dayIndex}.lineItems.${lineItemIndex}.details`}
                      control={control}
                      textFieldProps={{
                        multiline: true,
                        rows: 3,
                      }}
                    />
                  </Grid>
                </>
              )}
              {canEditAttendances &&
                attendancesFields.map((a, index) => <InvoiceFormLineItemAttendance key={index} attendanceIndex={index} {...props} />)}
            </Grid>
          </AccordionDetails>
        </Accordion>
      </Grid>
      <Grid item sm={2}>
        {componentsWatch.length > 1 && (
          <Grid container>
            {componentsFields.map((c, index) => (
              <Grid item sm={12} key={index}>
                <InvoiceFormLineItemComponent lineItemComponentIndex={index} {...props} disabled={disabled || !canEditItems} />
              </Grid>
            ))}
          </Grid>
        )}
        {componentsWatch.length <= 1 && (
          <FormInputCurrency
            name={`days.${dayIndex}.lineItems.${lineItemIndex}.priceItem`}
            label={isPricesNet ? i18next.t('invoice-lineitem-price-net') : i18next.t('invoice-lineitem-price-gross')}
            currency={version.priceList.currency}
            emptyValue={NO_PRICE}
            control={control}
            disabled={disabled || !canEditItems}
          />
        )}
      </Grid>
      <Grid item sm={1}>
        <FormInputCurrency
          name={`days.${dayIndex}.lineItems.${lineItemIndex}.priceNet`}
          emptyValue={NO_PRICE}
          label={i18next.t('invoice-lineitem-net')}
          currency={version.priceList.currency}
          control={control}
          disabled
        />
      </Grid>
      <Grid item sm={1}>
        <FormInputCurrency
          name={`days.${dayIndex}.lineItems.${lineItemIndex}.priceGross`}
          emptyValue={NO_PRICE}
          label={i18next.t('invoice-lineitem-gross')}
          currency={version.priceList.currency}
          control={control}
          disabled
        />
      </Grid>
      <Grid item sm={1}>
        {onEditInvoice && <IconButton onClick={() => onEditInvoice()}>
          <ReceiptIcon />
        </IconButton>}
      </Grid>
    </>
  );
}

function InvoiceFormLineItemAttendance({
  dayIndex,
  lineItemIndex,
  attendanceIndex,
  disabled,
  lineItemAttendanceChanged,
}: InvoiceFormLineItemAttendanceProps) {
  const { control, watch } = useFormContext<InvoiceFormType>();
  const isInitialRender = useRef(true);

  const showWatch = watch(`days.${dayIndex}.lineItems.${lineItemIndex}.attendances.${attendanceIndex}.show`);
  const profileIdWatch = watch(`days.${dayIndex}.lineItems.${lineItemIndex}.attendances.${attendanceIndex}.profileId`);
  useEffect(() => {
    if (isInitialRender.current) {
      isInitialRender.current = false;
      return;
    }
    if (disabled) return;
    lineItemAttendanceChanged(dayIndex, lineItemIndex, profileIdWatch, showWatch);
  }, [showWatch, profileIdWatch]);

  return (
    <>
      <Grid item sm={6}>
        <FormInputText name={`days.${dayIndex}.lineItems.${lineItemIndex}.attendances.${attendanceIndex}.profileName`} control={control} disabled />
      </Grid>
      <Grid item sm={3}>
        <FormInputCheckbox
          name={`days.${dayIndex}.lineItems.${lineItemIndex}.attendances.${attendanceIndex}.show`}
          label={i18next.t('invoice-lineitem-attendance-show')}
          control={control}
          disabled={disabled}
        />
      </Grid>
      <Grid item sm={3}>
        <FormInputCheckbox
          name={`days.${dayIndex}.lineItems.${lineItemIndex}.attendances.${attendanceIndex}.directPay`}
          label={i18next.t('invoice-lineitem-attendance-directpay')}
          control={control}
          disabled
        />
      </Grid>
    </>
  );
}

function InvoiceFormLineItemComponent(props: InvoiceFormLineItemComponentProps) {
  const { dayIndex, lineItemIndex, version, disabled, isPricesNet, canEditItems, lineItemComponentIndex } = props;
  const { control, watch } = useFormContext<InvoiceFormType>();

  const isInitialRender = useRef(true);

  const nameWatch = watch(`days.${dayIndex}.lineItems.${lineItemIndex}.components.${lineItemComponentIndex}.name`);
  const typeWatch = watch(`days.${dayIndex}.lineItems.${lineItemIndex}.components.${lineItemComponentIndex}.taxTypeId`);
  const priceWatch = watch(`days.${dayIndex}.lineItems.${lineItemIndex}.components.${lineItemComponentIndex}.price`);

  useEffect(() => {
    if (isInitialRender.current) {
      isInitialRender.current = false;
      return;
    }

    if (disabled || !canEditItems) return;
    props.lineItemCountChanged(dayIndex, lineItemIndex);
  }, [priceWatch]);

  const taxRate = props.quickPriceCalculator.getTaxRateForType(typeWatch);
  return (
    <FormInputCurrency
      name={`days.${dayIndex}.lineItems.${lineItemIndex}.components.${lineItemComponentIndex}.price`}
      label={`${taxRate ? taxRate.name : nameWatch} ${i18next.t(`invoice-lineitem-suffix-${isPricesNet ? 'net' : 'gross'}`)}`}
      currency={version.priceList.currency}
      control={control}
      emptyValue={NO_PRICE}
      required
      disabled={disabled}
    />
  );
}

interface InvoiceFormExtraLineItemProps extends InvoicePremiumFormProps {
  extraLineItemIndex: number;
  disabled: boolean;
  isPricesNet: boolean;
  onEditInvoice?: () => void;
  extraLineItemRemove: (index: number) => void;
}
interface InvoiceFormExtraLineItemComponentProps extends InvoiceFormExtraLineItemProps {
  extraLineItemComponentIndex: number;
}

function InvoiceFormExtraLineItemListener(props: { extraLineItemIndex: number; extraLineItemChanged: (index: number) => void }) {
  const { watch, control } = useFormContext<InvoiceFormType>();
  const isInitialRender = useRef(true);
  const countWatch = watch(`extraLineItems.${props.extraLineItemIndex}.count`);
  const priceWatch = watch(`extraLineItems.${props.extraLineItemIndex}.priceItem`);
  const dayWatch = watch(`extraLineItems.${props.extraLineItemIndex}.day`);

  const { fields: componentsFields } = useFieldArray({
    control,
    name: `extraLineItems.${props.extraLineItemIndex}.components`,
  });

  useEffect(() => {
    if (isInitialRender.current) {
      isInitialRender.current = false;
      return;
    }

    props.extraLineItemChanged(props.extraLineItemIndex);
  }, [countWatch, priceWatch, dayWatch]);

  return (
    <>
      {componentsFields.map((c, index) => (
        <InvoiceFormExtraLineItemComponentListener key={index} extraLineItemComponentIndex={index} {...props} />
      ))}
    </>
  );
}
function InvoiceFormExtraLineItemComponentListener(props: {
  extraLineItemIndex: number;
  extraLineItemComponentIndex: number;
  extraLineItemChanged: (index: number) => void;
}) {
  const { watch } = useFormContext<InvoiceFormType>();
  const isInitialRender = useRef(true);

  const priceWatch = watch(`extraLineItems.${props.extraLineItemIndex}.components.${props.extraLineItemComponentIndex}.price`);
  useEffect(() => {
    if (isInitialRender.current) {
      isInitialRender.current = false;
      return;
    }

    props.extraLineItemChanged(props.extraLineItemIndex);
  }, [priceWatch]);
  return null;
}

function InvoiceFormExtraLineItem(props: InvoiceFormExtraLineItemProps) {
  const { extraLineItemIndex, version, disabled, isPricesNet, onEditInvoice, extraLineItemRemove } = props;
  const { control, watch, setValue } = useFormContext<InvoiceFormType>();

  const [versionMediaQuery] = useLazyQuery(INVOICE_VIEW_OFFERVERSIONMEDIA_QUERY);

  const [mediaBase64, setMediaBase64] = useState<string>();

  const daysWatch = watch('days');
  const isDeletedWatch = watch(`extraLineItems.${extraLineItemIndex}.isDeleted`);
  const mediaWatch = watch(`extraLineItems.${extraLineItemIndex}.media`);
  const componentsWatch = watch(`extraLineItems.${extraLineItemIndex}.components`);

  const { fields: componentsFields } = useFieldArray({
    control,
    name: `extraLineItems.${extraLineItemIndex}.components`,
  });

  if (isDeletedWatch) return null;
  else
    return (
      <>
        <Grid item sm={2}>
          <FormInputDropdown
            name={`extraLineItems.${extraLineItemIndex}.day`}
            label={i18next.t('invoice-lineitem-day')}
            control={control}
            options={_.uniq([-1, ...daysWatch.map(d => d.day)])
              .sort()
              .map(day => {
                const dayLabel = `${formatOfferDay(day)} - ${formatDate(moment.utc(props.version.startDate, 'YYYY-MM-DD').add(day, 'days').toDate())}`;
                return {
                  value: day,
                  label: dayLabel,
                  filterText: dayLabel,
                };
              })}
            required
            disabled={disabled}
          />
        </Grid>
        <Grid item sm={1}>
          <FormInputNumber
            label={i18next.t('invoice-lineitem-count')}
            name={`extraLineItems.${extraLineItemIndex}.count`}
            control={control}
            disabled={disabled}
          />
        </Grid>
        <Grid item sm={2}>
          <FormInputText
            label={i18next.t('invoice-lineitem-header')}
            name={`extraLineItems.${extraLineItemIndex}.header`}
            control={control}
            disabled={disabled}
          />
        </Grid>
        {componentsWatch.length > 1 && (
          <Grid item sm={2}>
            <Grid container>
              {componentsFields.map((c, index) => (
                <Grid item sm={12} key={index}>
                  <InvoiceFormExtraLineItemComponent extraLineItemComponentIndex={index} {...props} disabled={disabled} />
                </Grid>
              ))}
            </Grid>
          </Grid>
        )}
        {componentsWatch.length <= 1 && (
          <Grid item sm={2}>
            <FormInputCurrency
              name={`extraLineItems.${extraLineItemIndex}.priceItem`}
              label={isPricesNet ? i18next.t('invoice-lineitem-price-net') : i18next.t('invoice-lineitem-price-gross')}
              emptyValue={NO_PRICE}
              currency={version.priceList.currency}
              control={control}
              disabled={disabled}
            />
          </Grid>
        )}
        <Grid item sm={1}>
          <FormInputCurrency
            name={`extraLineItems.${extraLineItemIndex}.priceNet`}
            emptyValue={NO_PRICE}
            label={i18next.t('invoice-lineitem-net')}
            currency={version.priceList.currency}
            control={control}
            disabled
          />
        </Grid>
        <Grid item sm={1}>
          <FormInputCurrency
            name={`extraLineItems.${extraLineItemIndex}.priceGross`}
            emptyValue={NO_PRICE}
            label={i18next.t('invoice-lineitem-gross')}
            currency={version.priceList.currency}
            control={control}
            disabled
          />
        </Grid>
        <Grid
          item
          sm={3}
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 0.5,
            flexWrap: 'nowrap',
            minWidth: 0,
          }}
        >
          {!mediaWatch && !disabled && (
            <FileUploadButton
              name={`uploadMedia_${extraLineItemIndex}`}
              accept={'image/*, application/pdf'}
              disabled={disabled}
              startIcon={<UploadIcon />}
              binary={(name, type) => true}
              buttonProps={{
                color: 'primary',
                variant: 'outlined',
                sx: {
                  whiteSpace: 'nowrap',
                  flexShrink: 0,
                  height: 45,
                  px: 1.5,
                },
              }}
              onChange={async (name, mimeType, base64, size) => {
                setValue(`extraLineItems.${extraLineItemIndex}.media`, { id: 0, name, mimeType, base64, size }, { shouldDirty: true });
              }}
            >
              {i18next.t('invoice-extras-upload-receipt')}
            </FileUploadButton>
          )}
          {mediaWatch && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                minWidth: 0,
                flex: 1,
                px: 1,
                py: 0.25,
                borderRadius: 1,
                bgcolor: 'action.hover',
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  minWidth: 0,
                  flex: 1,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
                title={mediaWatch.name}
              >
                {mediaWatch.name}
              </Typography>
              <IconButton
                size="small"
                sx={{ flexShrink: 0 }}
                onClick={() => {
                  if (!mediaWatch) return;
                  if (mediaWatch.base64) {
                    if (mediaWatch.mimeType.startsWith('image/')) {
                      setMediaBase64(mediaWatch.base64);
                    } else {
                      exportBase64ToFile(mediaWatch.base64, mediaWatch.mimeType, mediaWatch.name);
                    }
                  } else {
                    versionMediaQuery({
                      variables: {
                        mediaId: mediaWatch.id,
                      },
                    }).then(res => {
                      const base64 = res.data?.readWlOfferVersionMediaBase64;
                      if (base64) {
                        if (mediaWatch.mimeType.startsWith('image/')) {
                          setMediaBase64(base64);
                        } else {
                          exportBase64ToFile(base64, mediaWatch.mimeType, mediaWatch.name);
                        }
                      }
                    });
                  }
                }}
              >
                <VisibilityIcon fontSize="small" />
              </IconButton>
              {!disabled && (
                <IconButton
                  size="small"
                  sx={{ flexShrink: 0 }}
                  onClick={() => {
                    setValue(`extraLineItems.${extraLineItemIndex}.media`, null, { shouldDirty: true });
                  }}
                >
                  <ClearIcon fontSize="small" />
                </IconButton>
              )}
            </Box>
          )}
          {onEditInvoice && (
            <IconButton sx={{ flexShrink: 0 }} onClick={() => onEditInvoice()}>
              <ReceiptIcon />
            </IconButton>
          )}
          {!disabled && (
            <IconButton
              sx={{ flexShrink: 0 }}
              onClick={() => {
                extraLineItemRemove(extraLineItemIndex);
              }}
            >
              <DeleteIcon />
            </IconButton>
          )}
        </Grid>
        {mediaWatch && (
          <InformationDialog title={mediaWatch.name} open={!!mediaBase64} onConfirm={() => setMediaBase64(undefined)} maxWidth="md" fullWidth>
            <Box p={2}>
              <Grid container>
                <Grid item xs={12}>
                  {mediaBase64 && <img width="100%" src={`data:${mediaWatch.mimeType};base64,${mediaBase64}`} />}
                </Grid>
              </Grid>
            </Box>
          </InformationDialog>
        )}
      </>
    );
}

function InvoiceFormExtraLineItemComponent(props: InvoiceFormExtraLineItemComponentProps) {
  const { extraLineItemIndex, version, disabled, isPricesNet, extraLineItemComponentIndex } = props;
  const { control, watch } = useFormContext<InvoiceFormType>();

  const nameWatch = watch(`extraLineItems.${extraLineItemIndex}.components.${extraLineItemComponentIndex}.name`);
  const typeWatch = watch(`extraLineItems.${extraLineItemIndex}.components.${extraLineItemComponentIndex}.taxTypeId`);

  const taxRate = props.quickPriceCalculator.getTaxRateForType(typeWatch);
  return (
    <FormInputCurrency
      name={`extraLineItems.${extraLineItemIndex}.components.${extraLineItemComponentIndex}.price`}
      label={`${taxRate ? taxRate.name : nameWatch} ${i18next.t(`invoice-lineitem-suffix-${isPricesNet ? 'net' : 'gross'}`)}`}
      currency={version.priceList.currency}
      control={control}
      emptyValue={NO_PRICE}
      disabled={disabled}
    />
  );
}

interface InvoiceFormCancellationProps extends InvoicePremiumFormProps {
  ruleIndex: number;
  disabled: boolean;
}
interface InvoiceFormCancellationLineItemProps extends InvoiceFormCancellationProps {
  lineItemIndex: number;
}

function InvoiceFormCancellation(props: InvoiceFormCancellationProps) {
  const { ruleIndex, version, disabled } = props;
  const { control, watch, getValues, setValue } = useFormContext<InvoiceFormType>();

  const phaseWatch = watch(`cancellations.${ruleIndex}.rate`);
  const daysToEventWatch = watch(`cancellations.${ruleIndex}.daysToEvent`);

  const { fields: lineItemsFields } = useFieldArray({
    control,
    name: `cancellations.${ruleIndex}.lineItems`,
  });

  return (
    <Accordion>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography>
          {i18next.t('offerblock-cancellationitems-header-phase', {
            rate: phaseWatch !== undefined ? formatPercentage(phaseWatch, 0) : '?',
            daysToEvent: daysToEventWatch !== undefined ? daysToEventWatch : '?',
          })}
        </Typography>
      </AccordionSummary>
      <AccordionDetails>
        <Grid container>
          {lineItemsFields.map((li, index) => (
            <InvoiceFormCancellationLineItem key={index} lineItemIndex={index} {...props} />
          ))}
        </Grid>
      </AccordionDetails>
    </Accordion>
  );
}

function InvoiceFormCancellationLineItem(props: InvoiceFormCancellationLineItemProps) {
  const { ruleIndex, lineItemIndex, version } = props;
  const { control, watch, getValues } = useFormContext<InvoiceFormType>();

  const dayWatch = watch(`cancellations.${ruleIndex}.lineItems.${lineItemIndex}.day`);
  const skuWatch = watch(`cancellations.${ruleIndex}.lineItems.${lineItemIndex}.sku`);
  const headerWatch = watch(`cancellations.${ruleIndex}.lineItems.${lineItemIndex}.header`);

  const dayDate = moment.utc(version.startDate, 'YYYY-MM-DD').add(dayWatch, 'days').toDate();

  return (
    <>
      <Grid item sm={2}>
        {formatOfferDay(dayWatch!)} - {formatDate(dayDate)}
      </Grid>
      <Grid item sm={1}>
        <FormInputNumber name={`cancellations.${ruleIndex}.lineItems.${lineItemIndex}.count`} control={control} disabled />
      </Grid>
      <Grid item sm={8}>
        <Typography>
          {headerWatch} ({skuWatch})
        </Typography>
      </Grid>
      <Grid item sm={1}>
        <FormInputCurrency
          name={`cancellations.${ruleIndex}.lineItems.${lineItemIndex}.priceGross`}
          emptyValue={NO_PRICE}
          currency={version.priceList.currency}
          control={control}
          disabled
        />
      </Grid>
    </>
  );
}

/**
 * NO SHOWS tab: per participant (row) and per event day (column) the hotel records whether the
 * participant showed up.
 */
function InvoiceNoShowMatrix({
  event,
  version,
  eventDays,
  canEdit,
}: {
  event: InvoicePremiumFormProps['event'];
  version: InvoicePremiumFormProps['version'];
  eventDays: number[];
  canEdit: boolean;
}) {
  const { control, setValue } = useFormContext<InvoiceFormType>();
  const noShows = (useWatch({ control, name: 'noShows' }) || []) as NoShowFormType[];

  const attendanceDays = event ? event.attendances.flatMap(a => a.days.map(d => d.day)) : [];
  const days = Array.from(new Set([...eventDays, ...attendanceDays]))
    .filter(d => d >= 0)
    .sort((a, b) => a - b);

  if (!event || event.attendances.length === 0 || days.length === 0) {
    return (
      <Grid item xs={12}>
        <Alert severity="info">{i18next.t('invoice-noshows-empty')}</Alert>
      </Grid>
    );
  }

  const indexOf = (profileId: number, day: number) => noShows.findIndex(n => n.profileId === profileId && n.day === day);

  const toggle = (profileId: number, day: number) => {
    const i = indexOf(profileId, day);
    if (i < 0) return;
    setValue(`noShows.${i}.noShow`, !noShows[i].noShow, { shouldDirty: true });
  };

  return (
    <Grid item xs={12}>
      <Box sx={{ width: '100%', overflowX: 'auto' }}>
        <Table
          size="small"
          sx={{
            width: 'auto',
            tableLayout: 'auto',
            border: '1px solid',
            borderColor: 'divider',
            '& td, & th': {
              whiteSpace: 'nowrap',
              px: 1,
              py: 0.5,
              border: '1px solid',
              borderColor: 'divider',
            },
            '& th:first-of-type, & td:first-of-type': {
              position: 'sticky',
              left: 0,
              zIndex: 1,
              bgcolor: 'background.paper',
            },
          }}
        >
          <TableHead>
            <TableRow>
              <TableCell>{i18next.t('invoice-noshows-participant')}</TableCell>
              {days.map(day => (
                <TableCell key={day} align="center" sx={{ lineHeight: 1.2 }}>
                  {formatOfferDay(day)}
                  <br />
                  <Typography component="span" variant="caption" sx={{ color: 'text.secondary' }}>
                    {formatDate(moment(version.startDate).add(day, 'days').toDate())}
                  </Typography>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {event.attendances.map(a => (
              <TableRow key={a.id}>
                <TableCell>
                  {a.profile.firstname} {a.profile.lastname}
                </TableCell>
                {days.map(day => {
                  const aDay = a.days.find(d => d.day === day);
                  const registered = !!aDay && aDay.attending;
                  if (!registered) {
                    return (
                      <TableCell key={day} align="center" sx={{ color: 'text.disabled' }}>
                        &ndash;
                      </TableCell>
                    );
                  }
                  const i = indexOf(a.profile.id, day);
                  const noShow = i >= 0 ? noShows[i].noShow : false;
                  return (
                    <TableCell key={day} align="center">
                      <Chip
                        size="small"
                        label={noShow ? i18next.t('invoice-noshow-noshow') : i18next.t('invoice-noshow-present')}
                        color={noShow ? 'error' : 'success'}
                        variant={noShow ? 'filled' : 'outlined'}
                        onClick={canEdit ? () => toggle(a.profile.id, day) : undefined}
                        sx={{ minWidth: 84 }}
                      />
                    </TableCell>
                  );
                })}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>
    </Grid>
  );
}

export function InvoicePremiumFormEdit(props: InvoicePremiumFormProps) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const user = userSelector()!;
  const offerSource = props.offer.source;

  const [approveInvoice, { loading: approveInvoiceLoading }] = useMutation(INVOICE_APPROVE_PREMIUM_MUTATION);
  const [generateClientInvoices, { loading: generateClientInvoicesLoading }] = useMutation(INVOICE_GENERATE_MUTATION);

  const [selectedTab, setSelectedTab] = useState(0);
  const [offer] = useState(props.offer);
  const [version] = useState(props.version);
  const [event] = useState(props.event);

  const [hasSenderAccepted, setHasSenderAccepted] = useState(false);

  const [showGenerateInvoicesDialog, setShowGenerateInvoicesDialog] = useState(false);
  const [selectedCostCentres, setSelectedCostCentres] = useState<{ costCentre: string; name: string; selected: boolean }[]>([]);

  const [approvedId, setApprovedId] = useState(0);
  useEffect(() => {
    if (approvedId > 0) navigate(`/invoices/${offer.id}/${approvedId}`);
  }, [approvedId]);

  const [canEdit] = useState(
    user.isSeminargo
      ? version.status === EOfferVersionStatus.IV_WAITFORSEMAPPROVAL || version.status === EOfferVersionStatus.IV_WAITFORHOTEL
      : version.status === EOfferVersionStatus.IV_WAITFORHOTEL,
  );
  const [canEditItems] = useState(user.isSeminargo && version.status === EOfferVersionStatus.IV_WAITFORSEMAPPROVAL);
  const [canApprove] = useState(version.status === EOfferVersionStatus.IV_WAITFORHOTEL);
  const [canSEMApprove] = useState(user.isSeminargo && version.status === EOfferVersionStatus.IV_WAITFORSEMAPPROVAL);
  const [canEditInvoice] = useState(user.isSeminargo && version.status === EOfferVersionStatus.IV_WAITFORSEMAPPROVAL);

  const [editInvoice, setEditInvoice] = useState(false);
  const [editInvoiceItems, setEditInvoiceItems] = useState<
    { sku?: string | null; name: string; canChangeCommission: boolean; extraLineItemIndex?: number | null; commission: boolean }[]
  >([]);

  const [askSetShowForAttendance, setAskSetShowForAttendance] = useState<{ dayIndex: number; lineItemIndex: number; profileId: number } | undefined>(
    undefined,
  );

  const [generatedInvoices, setGeneratedInvoices] = useState<GenerateClientInvoicesMutation['generateClientInvoices']>(props.invoices);
  const [showGeneratedInvoiceId, setShowGeneratedInvoiceId] = useState<number | undefined>(
    props.invoices.length > 0 ? props.invoices[0].id : undefined,
  );

  const [rejectedReason] = useState(
    offer.history.find(h => h.status === EOfferVersionHistoryStatus.IV_REJECTED_CLIENT && h.offerVersion?.id === version.id)?.extraInfo || undefined,
  );

  const eventDays = getFullEventDayRange(version.contentBlocks);

  const toFormSchema = (offer: typeof props.offer, version: typeof props.version, event: typeof props.event): InvoiceFormType => {
    const accepted = (version.sender && addressSchema.isValidSync(version.sender)) || false;

    const _getLookupCode = (dayIndex: number, lineItemIndex: number): EProductLookupCode | null => {
      const sku = version.contentBlocks[dayIndex].lineItems[lineItemIndex].sku;
      if (!sku) return null;

      const lookupCode =
        props.quickPriceCalculator.allProductsDb!.find(p => p.sku === sku)?.lookupCode ||
        props.quickPriceCalculator.allBundlesDb!.find(b => b.sku === sku)?.lookupCode;
      if (!lookupCode) return null;

      return lookupCode as EProductLookupCode;
    };

    const _hasAttendances = (dayIndex: number, lineItemIndex: number): boolean => {
      const lookupCode = _getLookupCode(dayIndex, lineItemIndex);
      if (!lookupCode) return false;

      return isValidAttendanceLookupCode(lookupCode);
    };
    const _createAttendances = (dayIndex: number, lineItemIndex: number): AttendanceFormType[] => {
      if (!event || event.attendances.length === 0) return [];
      if (!_hasAttendances(dayIndex, lineItemIndex)) return [];

      const lookupCode = _getLookupCode(dayIndex, lineItemIndex)!;
      const lineItem = version.contentBlocks[dayIndex].lineItems[lineItemIndex];

      const attendances = event!.attendances
        .filter(a => a.days.findIndex(d => d.day === lineItem.day) >= 0)
        .map(a => {
          const aDay = a.days.find(d => d.day === lineItem.day)!;

          if (mapAttendanceRegistered(lookupCode as EAM_EventProductLookupCodes, aDay)) {
            return {
              profileId: a.profile.id,
              profileName: a.profile.lastname + ' ' + a.profile.firstname,
              day: lineItem.day,
              directPay: mapAttendanceDirectPay(lookupCode as EAM_EventProductLookupCodes, event!, a, aDay),
              show: mapAttendanceShow(lookupCode as EAM_EventProductLookupCodes, aDay),
            };
          }
        });
      return attendances.filter(a => a).map(a => a!);
    };

    let initDayCounter = 0;

    return {
      extraLineItems: version.contentBlocks.reduce<ExtraLineItemFormType[]>((agg, cb, cbIndex) => {
        if (cb.type === EOfferVersionContentBlockType.LINE_ITEMS && cb.lineItems.length > 0) {
          for (const li of cb.lineItems.filter(li => li.source === EOfferVersionContentBlockLineItemSource.EXTRA)) {
            agg.push({
              lineItemId: li.id,
              day: li.day,
              count: li.count,
              sku: li.sku,
              header: li.header,
              details: li.details,
              priceItem: li.priceItem,
              priceGross: li.priceGross,
              priceNet: li.priceNet,
              source: li.source,
              components: li.components.map(c => ({
                taxTypeId: c.type.id,
                name: c.type.name,
                price: c.price,
              })),
              taxes: li.taxes.map(t => ({
                taxTypeId: t.type.id,
                taxRateId: t.rate.id,
                name: t.type.name,
                rate: t.rate.rate,
                price: t.price,
              })),
              media: li.media
                ? {
                  id: li.media.id,
                  name: li.media!.name,
                  mimeType: li.media!.mimeType,
                  size: li.media!.size,
                }
                : null,
              hasAttendances: false,
              attendances: [],
              isDeleted: false,
              invoice: li.invoice ? {
                commission: !!li.invoice.commission,
                costCentre: li.invoice.costCentre,
                accountingArea: li.invoice.accountingArea,
                invoicingRule: li.invoice.invoicingRule,
              } : {
                commission: false,
                costCentre: null,
                accountingArea: null,
                invoicingRule: null,
              },
            });
          }
        }
        return [...agg];
      }, []),
      days: eventDays.map(day => ({
        day: day,
        accepted: false,
        expanded: 0 === initDayCounter++,
        lineItems: version.contentBlocks
          .reduce<((typeof version)['contentBlocks'][0]['lineItems'][0] & { cbIndex: number })[]>(
            (agg, cb, cbIndex) => [
              ...agg,
              ...(cb.type === EOfferVersionContentBlockType.LINE_ITEMS ? cb.lineItems.filter(li => li.day === day) : []).map(li => ({
                ...li,
                cbIndex,
              })),
            ],
            [],
          )
          .map((li, liIndex) =>
            (li.source === EOfferVersionContentBlockLineItemSource.OFFER || li.source === EOfferVersionContentBlockLineItemSource.MANUAL)
              ? {
                lineItemId: li.id,
                day: li.day,
                count: li.count,
                sku: li.sku,
                header: li.header,
                details: li.details,
                priceItem: li.priceItem,
                priceGross: li.priceGross,
                priceNet: li.priceNet,
                source: li.source,
                components: li.components.map(c => ({
                  taxTypeId: c.type.id,
                  name: c.type.name,
                  price: c.price,
                })),
                taxes: li.taxes.map(t => ({
                  taxRateId: t.rate.id,
                  name: t.type.name,
                  rate: t.rate.rate,
                  price: t.price,
                })),
                media: null,
                hasAttendances: _hasAttendances(li.cbIndex, liIndex),
                attendances: _createAttendances(li.cbIndex, liIndex),
                isChanged: false,
                invoice: li.invoice ? {
                  commission: !!li.invoice.commission,
                  costCentre: li.invoice.costCentre,
                  accountingArea: li.invoice.accountingArea,
                  invoicingRule: li.invoice.invoicingRule,
                } : {
                  commission: false,
                  costCentre: null,
                  accountingArea: null,
                  invoicingRule: null,
                },
              }
              : null,
          )
          .filter(li => li)
          .map(li => li!),
      })),
      cancellations: version.contentBlocks
        .map((cb, cbIndex) =>
          cb.type === EOfferVersionContentBlockType.CANCELLATION_ITEMS && cb.lineItems.length > 0
            ? {
              daysToEvent: cb.cancellationRule?.daysToEvent || NaN,
              rate: cb.cancellationRule?.rate || NaN,
              lineItems: cb.lineItems
                .map((li, liIndex) =>
                  li.source === EOfferVersionContentBlockLineItemSource.OFFER
                    ? {
                      lineItemId: li.id,
                      day: li.day,
                      count: li.count,
                      sku: li.sku,
                      header: li.header,
                      details: li.details,
                      priceItem: li.priceItem,
                      priceGross: li.priceGross,
                      priceNet: li.priceNet,
                      source: li.source,
                      components: li.components.map(c => ({
                        taxTypeId: c.type.id,
                        name: c.type.name,
                        price: c.price,
                      })),
                      taxes: li.taxes.map(t => ({
                        taxRateId: t.rate.id,
                        name: t.type.name,
                        rate: t.rate.rate,
                        price: t.price,
                      })),
                      media: null,
                      hasAttendances: false,
                      attendances: [],
                      isChanged: false
                    }
                    : null,
                )
                .filter(li => li)
                .map(li => li!),
            }
            : null,
        )
        .filter(cb => cb)
        .map(cb => cb!),
      noShows: !event
        ? []
        : event.attendances.flatMap(a =>
          a.days
            .filter(d => d.attending)
            .map(d => ({
              profileId: a.profile.id,
              day: d.day,
              noShow: !!d.noShow,
            })),
        ),
      agreedCancellationWaiveNet: version.agreedCancellationWaiveNet,
      manualCancellationWaiveNet: version.manualCancellationWaiveNet,
      ...(() => {
        const preview = (
          version as typeof version & { cancellationWaiverPreview?: PremiumInvoiceCancellationWaiverPreview | null }
        ).cancellationWaiverPreview;
        if (!preview) {
          return {
            cancellationWaiveMode: null as CancellationWaiveMode | null,
            cancellationChargedAmount: null as number | null,
          };
        }
        // New path stores Amount to invoice in manualCancellationWaiveNet.
        const inferred = inferCancellationWaiveMode(version.manualCancellationWaiveNet, preview.sum);
        const amountToInvoice = resolveCancellationAmountToInvoice({
          mode: inferred.mode,
          sum: preview.sum,
          chargedAmount: inferred.chargedAmount,
        });
        return {
          cancellationWaiveMode: inferred.mode,
          cancellationChargedAmount: inferred.chargedAmount,
          manualCancellationWaiveNet: amountToInvoice,
        };
      })(),
      totalPriceNet: version.totalPriceNet,
      totalPriceGross: version.totalPriceGross,
      totalTaxes: version.taxes.map(t => ({
        taxRateId: t.rate.id,
        name: t.type.name,
        rate: t.rate.rate,
        price: t.price,
      })),
      sender: {
        businessName: version.sender?.businessName || '',
        businessAddress1: version.sender?.businessAddress1 || '',
        businessAddress2: version.sender?.businessAddress2,
        businessAddress3: version.sender?.businessAddress3,
        businessAddress4: version.sender?.businessAddress4,
        businessZip: version.sender?.businessZip || '',
        businessCity: version.sender?.businessCity || '',
        businessCountry: version.sender?.businessCountry || ECountryCode.AT,
        billingUid: version.sender?.billingUid || '',
        businessEmail: version.sender?.businessEmail || user.email,
        businessPhone: version.sender?.businessPhone,
        contactName: version.sender?.contactName || user.name,
        comment: version.sender?.comment,
        accepted: accepted,
      },
      ...(() => {
        const defaultRate = getDefaultCommissionRate(offer.hotel, offer.publicCompany, version.offerDate);
        const commissionBase: EOfferCommissionBase =
          offer.commissionBase ?? (defaultRate.commissionBase === 'NET' ? EOfferCommissionBase.NET : EOfferCommissionBase.GROSS);
        const commissionPercentage =
          offer.commissionPercentage != null ? offer.commissionPercentage * 100 : defaultRate.commissionPercentage * 100;
        const commissionBaseAmount =
          offer.commissionBaseAmount != null
            ? offer.commissionBaseAmount
            : commissionBase === EOfferCommissionBase.NET
              ? version.totalPriceNet
              : version.totalPriceGross;
        return {
          commissionBase,
          commissionBaseAmount,
          commissionPercentage,
          commissionAmountNet:
            offer.commissionAmountNet != null
              ? offer.commissionAmountNet
              : roundPrice(commissionBaseAmount * (commissionPercentage / 100)),
          commissionAmountGross:
            offer.commissionAmountGross != null
              ? offer.commissionAmountGross
              : roundPrice(commissionBaseAmount * (commissionPercentage / 100) * 1.2),
          commissionAccepted: false,
        };
      })(),
    };
  };
  const validationSchemaResolved = useMemo(
    () => createValidationSchemaResolved(user.isSeminargo, canSEMApprove),
    [user.isSeminargo, canSEMApprove],
  );
  const form = useForm<InvoiceFormType>({
    mode: 'all',
    resolver: yupResolver(validationSchemaResolved) as any,
    defaultValues: toFormSchema(props.offer, props.version, props.event),
  });
  const {
    handleSubmit,
    control,
    trigger,
    reset,
    setValue,
    getValues,
    watch,
    formState: { errors: validationErrors, isValid, isDirty, isValidating, isSubmitting },
  } = form;

  const { fields: daysFields } = useFieldArray({
    control,
    name: 'days',
  });

  const {
    fields: extraLineItemsFields,
    append: extraLineItemAppend,
    remove: removeExtraLineItemField,
  } = useFieldArray({
    control,
    name: 'extraLineItems',
  });

  const { fields: cancellationsFields } = useFieldArray({
    control,
    name: 'cancellations',
  });

  const senderAcceptedWatch = watch('sender.accepted');
  const totalPriceNetWatch = watch('totalPriceNet');
  const totalPriceGrossWatch = watch('totalPriceGross');
  const extraLineItemsWatch = watch('extraLineItems');
  const activeExtraLineItemsCount = extraLineItemsWatch.filter(li => !li.isDeleted).length;
  const commissionBaseWatch = watch('commissionBase');
  const commissionBaseAmountWatch = watch('commissionBaseAmount');
  const commissionPercentageWatch = watch('commissionPercentage');
  const commissionAcceptedWatch = watch('commissionAccepted');

  useEffect(() => {
    setHasSenderAccepted(senderAcceptedWatch);
  }, [senderAcceptedWatch]);

  useEffect(() => {
    if (!canSEMApprove) return;
    if (commissionBaseWatch === offer.commissionBase && offer.commissionBaseAmount != null) {
      setValue('commissionBaseAmount', offer.commissionBaseAmount);
    } else if (commissionBaseWatch === EOfferCommissionBase.NET) {
      setValue('commissionBaseAmount', totalPriceNetWatch);
    } else if (commissionBaseWatch === EOfferCommissionBase.GROSS) {
      setValue('commissionBaseAmount', totalPriceGrossWatch);
    }
  }, [commissionBaseWatch]);

  useEffect(() => {
    if (!canSEMApprove) return;
    if (commissionBaseAmountWatch == null || commissionPercentageWatch == null) return;
    setValue('commissionAmountNet', roundPrice(commissionBaseAmountWatch * (commissionPercentageWatch / 100)));
    setValue('commissionAmountGross', roundPrice(commissionBaseAmountWatch * (commissionPercentageWatch / 100) * 1.2));
  }, [commissionBaseAmountWatch, commissionPercentageWatch]);

  const commissionRate = useMemo(() => {
    if (offer.commissionBase != null && offer.commissionPercentage != null) {
      return {
        commissionBase: offer.commissionBase,
        commissionPercentage: offer.commissionPercentage,
      };
    }
    return getDefaultCommissionRate(offer.hotel, offer.publicCompany, version.offerDate);
  }, [offer.commissionBase, offer.commissionPercentage, offer.hotel, offer.publicCompany, version.offerDate]);

  const commissionConfirmationLabel = useMemo(() => {
    const baseAmount = commissionRate.commissionBase === EOfferCommissionBase.NET ? totalPriceNetWatch : totalPriceGrossWatch;
    const commissionAmount = roundPrice(baseAmount * commissionRate.commissionPercentage);
    const agbUrl =
      offer.hotel.businessCountry === ECountryCode.DE
        ? 'https://www.seminargo.com/wp-content/uploads/2025/12/AGB_DE_Veranstaltungsvermittlung_2023.pdf'
        : 'https://www.seminargo.com/wp-content/uploads/2025/12/AGB_AT_Veranstaltungsvermittlung_2023.pdf';
    const textKey =
      commissionRate.commissionBase === EOfferCommissionBase.NET
        ? 'invoice-sender-accepted-commission-net'
        : 'invoice-sender-accepted-commission-gross';

    return (
      <>
        {i18next.t(textKey, {
          commissionPercentage: formatPercentage(commissionRate.commissionPercentage),
          commissionAmount: formatPrice(commissionAmount, version.priceList.currency),
          baseAmount: formatPrice(baseAmount, version.priceList.currency),
        })}{' '}
        <a href={agbUrl} target="_blank" rel="noopener noreferrer">
          {i18next.t('invoice-sender-accepted-commission-agb')}
        </a>
      </>
    );
  }, [commissionRate, totalPriceNetWatch, totalPriceGrossWatch, offer.hotel.businessCountry, version.priceList.currency]);

  const semCommissionInfoLabel = useMemo(() => {
    const baseAmount = commissionBaseAmountWatch ?? 0;
    const percentage = (commissionPercentageWatch ?? 0) / 100;
    const commissionAmountNet = roundPrice(baseAmount * percentage);
    const textKey =
      commissionBaseWatch === EOfferCommissionBase.NET ? 'invoice-commmission-info-text-net' : 'invoice-commmission-info-text-gross';
    return i18next.t(textKey, {
      commissionPercentage: formatPercentage(percentage),
      commissionAmountNet: formatPrice(commissionAmountNet, version.priceList.currency),
      commissionBaseAmount: formatPrice(baseAmount, version.priceList.currency),
    });
  }, [commissionBaseWatch, commissionBaseAmountWatch, commissionPercentageWatch, version.priceList.currency]);

  const manualCancellationWaiveNetWatch = watch('manualCancellationWaiveNet');
  const cancellationWaiveModeWatch = watch('cancellationWaiveMode');
  const cancellationChargedAmountWatch = watch('cancellationChargedAmount');

  const cancellationWaiverPreview = (
    version as typeof version & { cancellationWaiverPreview?: PremiumInvoiceCancellationWaiverPreview | null }
  ).cancellationWaiverPreview;

  const launchEditInvoiceDialog = () => {
    const lineItems = getValues('days').flatMap(d => d.lineItems).filter(li => !li.hasAttendances);
    const extraLineItems = getValues('extraLineItems');

    const skus = [...new Set(lineItems.map(li => li.sku))];
    const items = [
      ...skus.map(sku => {
        const firstLineItem = lineItems.find(li => li.sku === sku)!;

        const lookupCode = sku ? props.quickPriceCalculator.getLookupCodeForSKU(sku) : undefined;
        const canChangeCommission = lookupCode ? !COMMISSIONABLE_LOOKUP_CODES.includes(lookupCode) : true;

        return {
          sku,
          name: firstLineItem.header,
          canChangeCommission,
          commission: true,
        };
      }),
      ...extraLineItems
        .map((eli, index) => {
          if (eli.isDeleted) return null;
          return {
            sku: eli.sku,
            name: eli.header,
            extraLineItemIndex: index,
            canChangeCommission: true,
            commission: !!eli.invoice?.commission,
          };
        })
        .filter(eli => eli !== null)
        .map(eli => eli!),
    ];
    setEditInvoiceItems(items);
    setEditInvoice(true);
  };
  const syncEditInvoiceItems = () => {
    for (const item of editInvoiceItems) {
      if (item.extraLineItemIndex !== undefined && item.extraLineItemIndex !== null) {
        setValue(`extraLineItems.${item.extraLineItemIndex}.invoice.commission`, item.commission, { shouldDirty: true });
      } else if (item.sku !== undefined && item.sku !== null) {
        for (const [dayIndex, day] of getValues('days').entries()) {
          for (const [lineItemIndex, lineItem] of day.lineItems.entries()) {
            if (lineItem.sku === item.sku) {
              setValue(`days.${dayIndex}.lineItems.${lineItemIndex}.invoice.commission`, item.commission, { shouldDirty: true });
              setValue(`days.${dayIndex}.lineItems.${lineItemIndex}.isChanged`, true, { shouldDirty: true });
            }
          }
        }
      }
    }
    setEditInvoiceItems([]);
    setEditInvoice(false);
  };

  const lineItemCountChanged = (dayIndex: number, lineItemIndex: number) => {
    const lineItem = getValues(`days.${dayIndex}.lineItems.${lineItemIndex}`);
    const lineItemDate = moment(version.startDate).add(lineItem.day, 'days').toDate();

    try {
      const priceItem = lineItem.components.length > 1 ? lineItem.components.reduce((s: number, c) => s + c.price, 0) : lineItem.priceItem;
      const calcLineItem = props.quickPriceCalculator.getSKULineItem(
        lineItem.sku!,
        {
          date: lineItemDate,
          itemCount: lineItem.count,
          isPremium: offer.publicCompany ? offer.publicCompany.isPremium : false,
          publicCompanyId: offer.publicCompany ? offer.publicCompany.id : undefined
        },
        {
          price: priceItem,
          bundlePriceFromProduct: false,
          components: lineItem.components.map(c => ({
            taxTypeId: c.taxTypeId,
            price: c.price,
          })),
        },
      );
      if (!calcLineItem) return;

      //console.log('lineItemChanged', guestCounts, calcLineItem)
      setValue(`days.${dayIndex}.lineItems.${lineItemIndex}.priceItem`, priceItem, { shouldDirty: true });
      setValue(`days.${dayIndex}.lineItems.${lineItemIndex}.isChanged`, true, { shouldDirty: true });
      setValue(`days.${dayIndex}.lineItems.${lineItemIndex}.priceGross`, calcLineItem.priceGross, { shouldDirty: true });
      setValue(`days.${dayIndex}.lineItems.${lineItemIndex}.priceNet`, calcLineItem.priceNet, { shouldDirty: true });
      setValue(
        `days.${dayIndex}.lineItems.${lineItemIndex}.components`,
        calcLineItem.components.map(c => ({
          taxTypeId: c.type.id,
          name: c.type.name,
          price: c.price,
        })),
        { shouldDirty: true },
      );
      setValue(
        `days.${dayIndex}.lineItems.${lineItemIndex}.taxes`,
        calcLineItem.taxes.map(t => ({
          taxRateId: t.rate.id,
          name: t.type.name,
          rate: t.rate.rate,
          price: t.price,
        })),
        { shouldDirty: true },
      );
      setTimeout(() => onTotalsChanged());
    } catch (err) {
      dispatchException(dispatch, err);
    }
  };
  const lineItemAttendanceChanged = (dayIndex: number, lineItemIndex: number, profileId: number, show: boolean) => {
    if (!show && askSetShowForAttendance === undefined) {
      setAskSetShowForAttendance({ dayIndex, lineItemIndex, profileId });
    }

    const attendanceItems = getValues(`days.${dayIndex}.lineItems.${lineItemIndex}.attendances`);
    const itemCount = attendanceItems.filter(a => !a.directPay || (a.directPay && !a.show)).length;
    const lineItemCount = getValues(`days.${dayIndex}.lineItems.${lineItemIndex}.count`);
    if (lineItemCount !== itemCount) {
      setValue(`days.${dayIndex}.lineItems.${lineItemIndex}.count`, itemCount, { shouldDirty: true });
      setTimeout(() => lineItemCountChanged(dayIndex, lineItemIndex), 0);
    }
  };

  const extraLineItemAdd = (sku: string, media?: ExtraMediaFormType) => {
    try {
      const header = props.productTexts.getProductText({
        sku: sku,
        hotelId: offer.hotel.id,
        position: EContentProductDetailPosition.HEADER,
        language: offer.language,
      });
      const details = props.productTexts.getProductText({
        sku: sku,
        hotelId: offer.hotel.id,
        position: EContentProductDetailPosition.DETAILS,
        language: offer.language,
      });

      //TODO Guest Count
      const calcLineItem = props.quickPriceCalculator.getSKULineItem(sku,
        {
          date: moment(version.startDate).toDate(),
          itemCount: 1,
          isPremium: offer.publicCompany ? offer.publicCompany.isPremium : false,
          publicCompanyId: offer.publicCompany ? offer.publicCompany.id : undefined
        }, null, { allowNoPrices: true });
      if (calcLineItem) {
        extraLineItemAppend({
          lineItemId: 0,
          sku: calcLineItem.sku,
          count: calcLineItem.count,
          priceItem: calcLineItem.priceItem,
          priceGross: calcLineItem.priceGross,
          priceNet: calcLineItem.priceNet,
          source: EOfferVersionContentBlockLineItemSource.EXTRA,
          day: 0,
          header: header || calcLineItem.name,
          details: details || '',
          media: media || null,
          hasAttendances: false,
          attendances: [],
          components: calcLineItem.components.map(c => ({
            taxTypeId: c.type.id,
            name: c.type.name,
            price: c.price,
          })),
          taxes: calcLineItem.taxes.map(t => ({
            taxTypeId: t.type.id,
            taxRateId: t.rate.id,
            name: t.type.name,
            rate: t.rate.rate,
            price: t.price,
          })),
          isDeleted: false,
          invoice: {
            commission: false,
            costCentre: null,
            accountingArea: null,
            invoicingRule: null,
          },
        });
        setTimeout(() => onTotalsChanged(), 0);
      }
    } catch (err) {
      dispatchException(dispatch, err);
    }
  };

  const extraLineItemAddSpecialRequest = () => {
    try {
      const isPricesNet = version.priceList.isPricesNet;
      const lineItemDate = moment(version.startDate).add(0, 'days').toDate();
      const hotelCountry = props.quickPriceCalculator.hotel!.businessCountry;

      const taxTypes =
        props.quickPriceCalculator.taxRateSelector?.taxTypes?.filter(tt => {
          if (tt.name === 'NOTAX' || tt.name === '0%') return false;
          const rate = props.quickPriceCalculator.taxRateSelector?.getTaxRate({
            country: hotelCountry,
            date: lineItemDate,
            taxTypeId: tt.id,
          });
          return rate && rate.country === hotelCountry;
        }) || [];

      if (taxTypes.length === 0) return;

      if (taxTypes.length === 1) {
        const calcLineItem = props.quickPriceCalculator.getFreeStyleLineItem(1, 0, isPricesNet, taxTypes[0].id);
        if (!calcLineItem) return;
        extraLineItemAppend({
          lineItemId: 0,
          sku: null,
          count: 1,
          priceItem: 0,
          priceGross: 0,
          priceNet: 0,
          source: EOfferVersionContentBlockLineItemSource.EXTRA,
          day: 0,
          header: '',
          details: '',
          media: null,
          hasAttendances: false,
          attendances: [],
          components: calcLineItem.components.map(c => ({
            taxTypeId: c.type.id,
            name: c.type.name,
            price: c.price,
          })),
          taxes: calcLineItem.taxes.map(t => ({
            taxTypeId: t.type.id,
            taxRateId: t.rate.id,
            name: t.type.name,
            rate: t.rate.rate,
            price: t.price,
          })),
          isDeleted: false,
          invoice: {
            commission: false,
            costCentre: null,
            accountingArea: null,
            invoicingRule: null,
          },
        });
        setTimeout(() => onTotalsChanged(), 0);
        return;
      }

      const components = taxTypes.map(tt => ({
        taxTypeId: tt.id,
        name: tt.name,
        price: 0,
      }));

      const taxes = taxTypes.map(tt => {
        const taxRate = props.quickPriceCalculator.taxRateSelector?.getTaxRate({
          country: hotelCountry,
          date: lineItemDate,
          taxTypeId: tt.id,
        });
        if (!taxRate) {
          throw new Error(`No tax rate found for tax type ${tt.id} on ${moment(lineItemDate).format('YYYY-MM-DD')}`);
        }
        return {
          taxTypeId: tt.id,
          taxRateId: taxRate.id,
          name: tt.name,
          rate: taxRate.rate,
          price: 0,
        };
      });

      extraLineItemAppend({
        lineItemId: 0,
        sku: null,
        count: 1,
        priceItem: 0,
        priceGross: 0,
        priceNet: 0,
        source: EOfferVersionContentBlockLineItemSource.EXTRA,
        day: 0,
        header: '',
        details: '',
        media: null,
        hasAttendances: false,
        attendances: [],
        components,
        taxes,
        isDeleted: false,
        invoice: {
          commission: false,
          costCentre: null,
          accountingArea: null,
          invoicingRule: null,
        },
      });
      setTimeout(() => onTotalsChanged(), 0);
    } catch (err) {
      dispatchException(dispatch, err);
    }
  };

  const extraLineItemChanged = (eliIndex: number) => {
    const lineItem = getValues(`extraLineItems.${eliIndex}`);
    const lineItemDate = moment(version.startDate).add(lineItem.day, 'days').toDate();
    const isPricesNet = version.priceList.isPricesNet;
    const isMultiComponentFreeText = !lineItem.sku && lineItem.components.length > 1;

    if (isMultiComponentFreeText) {
      try {
        let totalPriceNet = 0;
        let totalPriceGross = 0;

        lineItem.components.forEach(c => {
          const taxInfo = lineItem.taxes.find(t => t.taxTypeId === c.taxTypeId);
          if (taxInfo) {
            const componentPriceNet = isPricesNet ? c.price : c.price / (1 + taxInfo.rate);
            const componentPriceGross = isPricesNet ? c.price * (1 + taxInfo.rate) : c.price;
            totalPriceNet += componentPriceNet * lineItem.count;
            totalPriceGross += componentPriceGross * lineItem.count;
          }
        });

        setValue(`extraLineItems.${eliIndex}.priceItem`, lineItem.components.reduce((s: number, c) => s + c.price, 0), { shouldDirty: true });
        setValue(`extraLineItems.${eliIndex}.priceGross`, totalPriceGross, { shouldDirty: true });
        setValue(`extraLineItems.${eliIndex}.priceNet`, totalPriceNet, { shouldDirty: true });

        const newTaxes = lineItem.components
          .map(c => {
            const taxInfo = lineItem.taxes.find(t => t.taxTypeId === c.taxTypeId);
            if (!taxInfo) return null;
            const componentPriceNet = isPricesNet ? c.price : c.price / (1 + taxInfo.rate);
            const componentPriceGross = isPricesNet ? c.price * (1 + taxInfo.rate) : c.price;
            return {
              taxTypeId: c.taxTypeId,
              taxRateId: taxInfo.taxRateId,
              name: taxInfo.name,
              rate: taxInfo.rate,
              price: (componentPriceGross - componentPriceNet) * lineItem.count,
            };
          })
          .filter((tax): tax is NonNullable<typeof tax> => tax !== null);

        setValue(`extraLineItems.${eliIndex}.taxes`, newTaxes, { shouldDirty: true });
        setTimeout(() => onTotalsChanged(), 0);
        return;
      } catch (err) {
        dispatchException(dispatch, err);
        return;
      }
    }

    try {
      const priceItem = lineItem.components.length > 1 ? lineItem.components.reduce((s: number, c) => s + c.price, 0) : lineItem.priceItem;

      let calcLineItem = null;
      if (lineItem.sku) {
        calcLineItem = props.quickPriceCalculator.getSKULineItem(
          lineItem.sku,
          {
            date: lineItemDate,
            itemCount: lineItem.count,
            isPremium: offer.publicCompany ? offer.publicCompany.isPremium : false,
            publicCompanyId: offer.publicCompany ? offer.publicCompany.id : undefined
          },
          {
            price: priceItem,
            bundlePriceFromProduct: false,
            components: lineItem.components.map(c => ({
              taxTypeId: c.taxTypeId,
              price: c.price,
            }))
          },
        );
      } else if (lineItem.taxes[0]?.taxTypeId) {
        calcLineItem = props.quickPriceCalculator.getFreeStyleLineItem(
          lineItem.count,
          priceItem,
          isPricesNet,
          lineItem.taxes[0].taxTypeId,
        );
      }
      if (!calcLineItem) return;

      setValue(`extraLineItems.${eliIndex}.priceItem`, priceItem, { shouldDirty: true });
      setValue(`extraLineItems.${eliIndex}.priceGross`, calcLineItem.priceGross, { shouldDirty: true });
      setValue(`extraLineItems.${eliIndex}.priceNet`, calcLineItem.priceNet, { shouldDirty: true });
      setValue(
        `extraLineItems.${eliIndex}.components`,
        calcLineItem.components.map(c => ({
          taxTypeId: c.type.id,
          name: c.type.name,
          price: c.price,
        })),
        { shouldDirty: true },
      );
      setValue(
        `extraLineItems.${eliIndex}.taxes`,
        calcLineItem.taxes.map(t => ({
          taxTypeId: t.type.id,
          taxRateId: t.rate.id,
          name: t.type.name,
          rate: t.rate.rate,
          price: t.price,
        })),
        { shouldDirty: true },
      );
      setTimeout(() => onTotalsChanged(), 0);
    } catch (err) {
      dispatchException(dispatch, err);
    }
  };
  const onTotalsChanged = () => {
    const preview = cancellationWaiverPreview;
    if (preview) {
      // SM-2791 + waiver path: services only, then add untaxed Amount to invoice (ignore classic CANCELLATION_ITEMS).
      const services = calculateTotals({
        agreedCancellationWaiveNet: 0,
        manualCancellationWaiveNet: 0,
        contentBlocks: [
          ...getValues('days').map(d => ({ type: EOfferVersionContentBlockType.LINE_ITEMS, lineItems: d.lineItems })),
          {
            type: EOfferVersionContentBlockType.LINE_ITEMS,
            lineItems: getValues('extraLineItems').filter(li => !li.isDeleted),
          },
        ],
      });
      const mode = (getValues('cancellationWaiveMode') || 'CHARGE') as CancellationWaiveMode;
      let chargedAmount = getValues('cancellationChargedAmount');
      if (mode === 'PARTIAL' && chargedAmount != null && chargedAmount > preview.sum) {
        chargedAmount = preview.sum;
        setValue('cancellationChargedAmount', chargedAmount);
      }
      const amountToInvoice = resolveCancellationAmountToInvoice({
        mode,
        sum: preview.sum,
        chargedAmount,
      });
      if (getValues('manualCancellationWaiveNet') !== amountToInvoice) {
        setValue('manualCancellationWaiveNet', amountToInvoice);
      }
      setValue('totalPriceNet', services.totalPriceNet === NO_PRICE ? NO_PRICE : services.totalPriceNet + amountToInvoice);
      setValue('totalPriceGross', services.totalPriceGross === NO_PRICE ? NO_PRICE : services.totalPriceGross + amountToInvoice);
      setValue('totalTaxes', _.orderBy(services.totalTaxes, [t => t.name], 'asc'));
      trigger();
      return;
    }

    const { totalPriceNet, totalPriceGross, totalTaxes } = calculateTotals({
      agreedCancellationWaiveNet: getValues('agreedCancellationWaiveNet'),
      manualCancellationWaiveNet: getValues('manualCancellationWaiveNet'),
      contentBlocks: [
        ...getValues('days').map(d => ({ type: EOfferVersionContentBlockType.LINE_ITEMS, lineItems: d.lineItems })),
        {
          type: EOfferVersionContentBlockType.LINE_ITEMS,
          lineItems: getValues('extraLineItems').filter(li => !li.isDeleted),
        },
        ...getValues('cancellations').map(d => ({ type: EOfferVersionContentBlockType.CANCELLATION_ITEMS, lineItems: d.lineItems })),
      ],
    });
    setValue('totalPriceNet', totalPriceNet);
    setValue('totalPriceGross', totalPriceGross);
    setValue('totalTaxes', _.orderBy(totalTaxes, [t => t.name], 'asc'));
    trigger();
  };

  const syncCancellationAmountToInvoice = (mode: CancellationWaiveMode, chargedAmount?: number | null) => {
    if (!cancellationWaiverPreview) return;
    const amount = resolveCancellationAmountToInvoice({
      mode,
      sum: cancellationWaiverPreview.sum,
      chargedAmount: chargedAmount ?? getValues('cancellationChargedAmount'),
    });
    setValue('cancellationWaiveMode', mode, { shouldDirty: true });
    setValue('manualCancellationWaiveNet', amount, { shouldDirty: true });
    if (mode === 'PARTIAL' && (chargedAmount == null || chargedAmount === undefined)) {
      setValue('cancellationChargedAmount', amount, { shouldDirty: true });
    }
    setTimeout(() => onTotalsChanged(), 0);
  };

  useEffect(() => {
    onTotalsChanged();
  }, [manualCancellationWaiveNetWatch, cancellationWaiveModeWatch, cancellationChargedAmountWatch]);

  useEffect(() => {
    trigger();
  }, [trigger]);

  const onSubmitApproveHotel = async (values: InvoiceFormType) => onSubmit(values, true, false, false, false);
  const onSubmitCancelHotel = async (values: InvoiceFormType) => onSubmit(values, false, true, false, false);
  const onSubmitApproveSEM = async (values: InvoiceFormType) => onSubmit(values, false, false, true, false);
  const onSubmitRejectSEM = async (values: InvoiceFormType) => onSubmit(values, false, false, false, true);
  const onSubmitPrintProforma = async (values: InvoiceFormType) => {
    if (isDirty) {
      const offerVersionId = await onSubmit(values, false, false, false, false);
      if (!offerVersionId) return;
      window.open(getFullBackendUrl(`/backend/pdf/downloadOffer/${offerVersionId}`), '_blank');
    } else {
      window.open(getFullBackendUrl(`/backend/pdf/downloadOffer/${version.id}`), '_blank');
    }
  };

  const onSubmit = async (values: InvoiceFormType, approveAsHotel: boolean, cancelAsHotel: boolean, approveAsSEM: boolean, rejectAsSEM: boolean) => {
    if (!canEdit) return;
    if (approveAsHotel && !canApprove) return;
    if (cancelAsHotel && !canApprove) return;
    if (approveAsSEM && !canSEMApprove) return;
    if (rejectAsSEM && !canSEMApprove) return;

    const attendances: WLInvoiceAttendanceInput[] = [];
    const _get_a = (day: number, profileId: number) => {
      let r = attendances.find(a => a.day === day && a.profileId === profileId);
      if (!r) {
        r = {
          day: day,
          profileId: profileId,
        };
        attendances.push(r);
      }
      return r;
    };

    for (const day of values.days) {
      for (const li of day.lineItems.filter(li => li.hasAttendances)) {
        if (!li.sku) continue;

        const lookupCode =
          props.quickPriceCalculator.allProductsDb!.find(p => p.sku === li.sku)?.lookupCode ||
          props.quickPriceCalculator.allBundlesDb!.find(b => b.sku === li.sku)?.lookupCode;
        if (!lookupCode) continue;

        mapShowAttendance(
          lookupCode as EAM_EventProductLookupCodes,
          li.attendances.map(a => a.show),
          li.attendances.map(a => _get_a(day.day, a.profileId)),
        );
      }
    }

    // NO SHOWS tab: day-level presence flag
    for (const ns of values.noShows) {
      _get_a(ns.day, ns.profileId).noShow = ns.noShow;
    }

    try {
      const res = await approveInvoice({
        variables: {
          offerVersionId: props.version.id,
          attendances: attendances,
          replaceLineItems: values.days.reduce(
            (agg, day) => [
              ...agg,
              ...day.lineItems
                .filter(li => li.isChanged)
                .map(eli => ({
                  day: eli.day,
                  sku: eli.sku,
                  header: eli.header,
                  details: eli.details || '',
                  count: eli.count,
                  sequence: 0,
                  priceItem: eli.priceItem,
                  priceNet: eli.priceNet,
                  priceGross: eli.priceGross,
                  source: eli.source,
                  components: eli.components.map(elic => ({
                    taxTypeId: elic.taxTypeId,
                    price: elic.price,
                  })),
                  taxes: eli.taxes.map(elit => ({
                    taxRateId: elit.taxRateId,
                    price: elit.price,
                  })),
                  invoice: {
                    commission: !!eli.invoice?.commission,
                    costCentre: eli.invoice?.costCentre,
                    accountingArea: eli.invoice?.accountingArea,
                    invoicingRule: eli.invoice?.invoicingRule,
                  }
                })),
            ],
            [] as WLOfferVersionContentBlockLineItemInput[],
          ),
          extraLineItems: values.extraLineItems
            .filter(eli => !eli.isDeleted)
            .map(eli => ({
              day: eli.day,
              sku: eli.sku,
              header: eli.header,
              details: eli.details || '',
              count: eli.count,
              sequence: 0,
              priceItem: eli.priceItem,
              priceNet: eli.priceNet,
              priceGross: eli.priceGross,
              source: eli.source,
              components: eli.components.map(elic => ({
                taxTypeId: elic.taxTypeId,
                price: elic.price,
              })),
              taxes: eli.taxes.map(elit => ({
                taxRateId: elit.taxRateId,
                price: elit.price,
              })),
              ...(eli.media && eli.media.id && !eli.media.base64 ? { mediaId: eli.media.id } : {}),
              media:
                eli.media && eli.media.base64
                  ? {
                    name: eli.media.name,
                    mimeType: eli.media.mimeType,
                    base64: eli.media.base64,
                    size: eli.media.size,
                  }
                  : undefined,
              invoice: {
                commission: !!eli.invoice?.commission,
                costCentre: eli.invoice?.costCentre,
                accountingArea: eli.invoice?.accountingArea,
                invoicingRule: eli.invoice?.invoicingRule,
              },
            })),
          deleteLineItemIds: values.extraLineItems.filter(eli => eli.lineItemId > 0 && eli.isDeleted).map(eli => eli.lineItemId),
          sender: {
            businessName: values.sender.businessName,
            businessAddress1: values.sender.businessAddress1,
            businessAddress2: values.sender.businessAddress2,
            businessAddress3: values.sender.businessAddress3,
            businessAddress4: values.sender.businessAddress4,
            businessZip: values.sender.businessZip,
            businessCity: values.sender.businessCity,
            businessCountry: values.sender.businessCountry,
            billingUid: values.sender.billingUid,
            businessEmail: values.sender.businessEmail,
            businessPhone: values.sender.businessPhone,
            comment: values.sender.comment,
            contactName: values.sender.contactName,
          },
          manualCancellationWaiveNet: values.manualCancellationWaiveNet,
          ...(approveAsSEM
            ? {
                commissionBase: values.commissionBase!,
                commissionBaseAmount: values.commissionBaseAmount!,
                commissionPercentage: values.commissionPercentage! / 100,
              }
            : {}),
          approveAsHotel: approveAsHotel,
          cancelAsHotel: cancelAsHotel,
          approveAsSEM: approveAsSEM,
          rejectAsSEM: rejectAsSEM,
        },
        update: cache => {
          EVICT_INVOICE_QUERIES(cache);
        },
        awaitRefetchQueries: true,
        refetchQueries: REFETCH_INVOICE_QUERIES(props.offer.id, props.version.id),
      });
      reset(toFormSchema(offer, res.data!.approvePremiumInvoice, event));
      setApprovedId(res.data!.approvePremiumInvoice.id);
      if (approveAsHotel) dispatchMessage(dispatch, i18next.t('invoice-status-approved'));
      else if (cancelAsHotel) dispatchMessage(dispatch, i18next.t('invoice-status-cancelled'));
      else if (approveAsSEM) dispatchMessage(dispatch, i18next.t('invoice-status-semconfirmed'));
      else if (rejectAsSEM) dispatchMessage(dispatch, i18next.t('invoice-status-semrejected'));
      return res.data!.approvePremiumInvoice.id;
    } catch (err) {
      dispatchException(dispatch, err);
    }
  };

  return (
    <>
      <Helmet>
        <title>{formatDocumentTitle([i18next.t(`invoices-list-page-title-${props.offer.source}`), props.offer])}</title>
      </Helmet>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Typography variant="h6">
            {props.version.extRefCode || props.offer.refCode} - {props.offer.bookingTitle}
          </Typography>
          <Typography variant="body2" sx={{ mt: 0.5 }}>
            {props.version.status === 'CR_WAITFORHOTELAPPROVAL' && props.offer.status === 'APPROVED'
              ? i18next.t('enums-EOfferVersionStatus-CR_WAITFORHOTELAPPROVAL-RESERVATION')
              : i18next.t(`enums-EOfferVersionStatus-${props.version.status}`)}
          </Typography>
        </Grid>
        <Grid item xs={12}>
          {canApprove && watch('totalPriceGross') !== 0 && (
            <ConfirmationButton
              sx={{ marginRight: 2 }}
              variant="contained"
              confirmationQuestion={i18next.t('invoices-confirm-question')}
              confirmationTitle={i18next.t('invoices-confirm-question-title')}
              doubleConfirmationQuestion={!hasSenderAccepted ? i18next.t('invoices-confirm-question-force') : undefined}
              startIcon={isSubmitting ? <CircularProgress size={24} /> : <ThumbUpIcon />}
              disabled={!isValid || isSubmitting}
              onConfirm={async () => {
                const valid = await trigger();
                if (valid) {
                  await handleSubmit(onSubmitApproveHotel)();
                  navigate('/offers/lister')
                }
              }}
            >
              {i18next.t('invoices-confirm-title')}
            </ConfirmationButton>
          )}
          {canApprove && watch('totalPriceGross') === 0 && (
            <ConfirmationButton
              sx={{ marginRight: 2 }}
              variant="contained"
              confirmationQuestion={i18next.t('invoices-cancel-question')}
              confirmationTitle={i18next.t('invoices-cancel-question-title')}
              startIcon={isSubmitting ? <CircularProgress size={24} /> : <CancelIcon />}
              disabled={!isValid || isSubmitting}
              onConfirm={async () => {
                const valid = await trigger();
                if (valid) {
                  handleSubmit(onSubmitCancelHotel)();
                }
              }}
            >
              {i18next.t('invoices-cancel-title')}
            </ConfirmationButton>
          )}
          {canSEMApprove && (
            <>
              <ConfirmationButton
                sx={{ marginRight: 2 }}
                variant="contained"
                confirmationQuestion={i18next.t('invoices-semconfirm-question')}
                confirmationTitle={i18next.t('invoices-semconfirm-question-title')}
                startIcon={isSubmitting ? <CircularProgress size={24} /> : <ThumbUpIcon />}
                disabled={!isValid || isSubmitting || !commissionAcceptedWatch}
                onConfirm={async () => {
                  const valid = await trigger();
                  if (valid) {
                    handleSubmit(onSubmitApproveSEM)();
                  }
                }}
              >
                {i18next.t('invoices-semconfirm-title')}
              </ConfirmationButton>
              <ConfirmationButton
                sx={{ marginRight: 2 }}
                variant="contained"
                color="secondary"
                confirmationQuestion={i18next.t('invoices-semreject-question')}
                confirmationTitle={i18next.t('invoices-semreject-question-title')}
                startIcon={isSubmitting ? <CircularProgress size={24} /> : <ThumbDownIcon />}
                disabled={!isValid || isSubmitting}
                onConfirm={async () => {
                  const valid = await trigger();
                  if (valid) {
                    handleSubmit(onSubmitRejectSEM)();
                  }
                }}
              >
                {i18next.t('invoices-semreject-title')}
              </ConfirmationButton>
              <Button
                sx={{ marginRight: 2 }}
                variant="contained"
                color="secondary"
                startIcon={generateClientInvoicesLoading ? <CircularProgress size={24} /> : <DownloadIcon />}
                onClick={async () => {
                  setSelectedCostCentres(
                    (props.invoiceCostCentres || []).map(l => ({
                      costCentre: l.costCentre,
                      name: l.name,
                      selected: true,
                    })),
                  );
                  setShowGenerateInvoicesDialog(true);
                }}
              >
                {i18next.t('invoices-generate-client-invoices')}
              </Button>
              <ConfirmationDialog
                title={i18next.t('invoices-generate-client-invoices')}
                open={showGenerateInvoicesDialog}
                setOpen={setShowGenerateInvoicesDialog}
                keepOpenOnConfirm={true}
                onConfirm={async () => {
                  setShowGenerateInvoicesDialog(false);
                  try {
                    const { data } = await generateClientInvoices({
                      variables: {
                        offerVersionId: version.id,
                        costCentres: selectedCostCentres.filter(s => s.selected).map(s => s.costCentre),
                      },
                      update: cache => {
                        EVICT_INVOICE_QUERIES(cache);
                      },
                      awaitRefetchQueries: true,
                      refetchQueries: REFETCH_INVOICE_QUERIES(props.offer.id, props.version.id),
                    });
                    if (data?.generateClientInvoices) {
                      setGeneratedInvoices(data!.generateClientInvoices);
                      if (data!.generateClientInvoices.length > 0) {
                        setShowGeneratedInvoiceId(data!.generateClientInvoices[0].id);
                      }
                      setSelectedTab(3);
                    }
                  } catch (err) {
                    dispatchException(dispatch, err);
                  }
                }}
              >
                <Grid container spacing={2}>
                  {selectedCostCentres.length === 0 && (
                    <Grid item xs={12}>
                      {i18next.t('invoices-generate-client-invoices-no-costcentres')}
                    </Grid>
                  )}
                  {selectedCostCentres.length > 0 && (
                    <>
                      <Grid item xs={12}>
                        {i18next.t('invoices-generate-client-invoices-split-costcentres')}
                      </Grid>
                      {selectedCostCentres.map((cc, i) => (
                        <Grid key={i} item xs={12}>
                          <Checkbox
                            checked={cc.selected}
                            onChange={() => {
                              const cc = [...selectedCostCentres];
                              cc[i].selected = !cc[i].selected;
                              setSelectedCostCentres(cc);
                            }}
                          />
                          {cc.costCentre} ({cc.name})
                        </Grid>
                      ))}
                    </>
                  )}
                </Grid>
              </ConfirmationDialog>
            </>
          )}
          {!canApprove && (
            <Button
              sx={{ marginRight: 2 }}
              variant="contained"
              color="secondary"
              startIcon={<DownloadIcon />}
              disabled={!isValid || isSubmitting}
              onClick={async () => {
                const valid = await trigger();
                if (valid) {
                  handleSubmit(onSubmitPrintProforma)();
                }
              }}
            >
              {i18next.t('invoices-download-proforma-invoice')}
            </Button>
          )}
        </Grid>
        <Grid item xs={12}>
          <ContactPersonTabsWrapper responsibleUser={props.offer.responsibleUser}>
            <UnsavedChangesPrompt isDirty={isDirty} />
            <FormProvider {...form}>
              <CustomTabsControlled
                selected={selectedTab}
                handleChange={selected => setSelectedTab(selected)}
                headers={[
                  i18next.t('invoice-noshows-tab'),
                  i18next.t('invoice-lineitems-tab'),
                  i18next.t('invoice-info-tab'),
                  i18next.t('invoice-generated-tab'),
                  i18next.t('invoice-tasks-tab'),
                  i18next.t('offer-history-tab'),
                ]}
                hidden={[false, false, false, false, !(offerSource === EOfferSource.LISTER && (user.isSeminargo || user.isRoot)), false]}
                icons={[
                  undefined,
                  canEdit &&
                    ((canApprove
                      ? Object.keys(validationErrors).findIndex(v => v.startsWith('extraLineItems')) >= 0 ||
                      Object.keys(validationErrors).findIndex(v => v.startsWith('cancellations')) >= 0
                      : Object.keys(validationErrors).findIndex(v => v.startsWith('days')) >= 0 ||
                      Object.keys(validationErrors).findIndex(v => v.startsWith('extraLineItems')) >= 0 ||
                      Object.keys(validationErrors).findIndex(v => v.startsWith('cancellations')) >= 0)) ? (
                    <ErrorIcon />
                  ) : undefined,
                  canEdit && ((canApprove && !hasSenderAccepted) || Object.keys(validationErrors).findIndex(v => v.startsWith('sender')) >= 0) ? (
                    <ErrorIcon />
                  ) : undefined,
                  undefined,
                  undefined,
                  undefined,
                ]}
                tabs={[
                  <Grid container spacing={2}>
                    <InvoiceNoShowMatrix version={version} event={event} eventDays={eventDays} canEdit={canApprove && !isSubmitting} />
                  </Grid>,
                  <Grid container spacing={2}>
                    {[EOfferVersionStatus.IV_WAITFORHOTEL, EOfferVersionStatus.IV_WAITFORSEMAPPROVAL].includes(version.status) && (
                      <InvoiceReadonlyBookedServices version={version} quickPriceCalculator={props.quickPriceCalculator} />
                    )}
                    {rejectedReason && (
                      <Grid item xs={12}>
                        <Alert severity="error">
                          {i18next.t('invoice-client-rejected')}
                          <br />
                          {i18next.t('invoice-client-rejected-reason')} {rejectedReason}
                        </Alert>
                      </Grid>
                    )}
                    {!canApprove && (
                      <>
                        <Grid item xs={12} sx={{ pb: 0.5 }}>
                          <Typography variant="subtitle1" fontWeight={700}>{i18next.t('invoice-booked-services-current-title')}</Typography>
                        </Grid>

                        {daysFields.map((day, index) => (
                          <Grid item xs={12} key={index}>
                            <InvoiceFormDay
                              dayIndex={index}
                              disabled={!canEdit || isSubmitting}
                              canEditItems={canEditItems && !isSubmitting}
                              onEditInvoice={(canEditInvoice && !isSubmitting) ? () => launchEditInvoiceDialog() : undefined}
                              canAccept={canApprove}
                              isPricesNet={version.priceList.isPricesNet}
                              setHasAllAccepted={() => undefined}
                              lineItemAttendanceChanged={lineItemAttendanceChanged}
                              lineItemCountChanged={lineItemCountChanged}
                              {...props}
                            />
                          </Grid>
                        ))}
                      </>
                    )}
                    {editInvoice && editInvoiceItems.length > 0 &&
                      <SimpleDialog
                        title={i18next.t('invoice-invoice-edit-title')}
                        open={editInvoice}
                        setOpen={setEditInvoice}
                        fullWidth
                        maxWidth="lg"
                        customActions={<>
                          <Button
                            variant="contained"
                            color="secondary"
                            onClick={() => {
                              setEditInvoiceItems([]);
                              setEditInvoice(false);
                            }}
                            startIcon={<CancelIcon />}
                          >
                            {i18next.t('cancel')}
                          </Button>
                          <Button
                            variant="contained"
                            color="primary"
                            onClick={() => {
                              syncEditInvoiceItems();
                            }}
                            startIcon={<SaveIcon />}
                          >
                            {i18next.t('apply')}
                          </Button>
                        </>}
                      >
                        <Grid container spacing={2}>
                          <Grid item xs={12}>
                            <SimpleTable fullWidth
                              rows={editInvoiceItems.map((item, index) => [
                                item.name,
                                <FormControl>
                                  <FormControlLabel
                                    control={<Checkbox
                                      checked={item.commission}
                                      onChange={() => setEditInvoiceItems(editInvoiceItems.map((oe, oindex) => oindex === index ? ({ ...oe, commission: !oe.commission }) : ({ ...oe })))}
                                      disabled={!canEditInvoice || !item.canChangeCommission}
                                    />}
                                    label={i18next.t('invoice-invoice-edit-commission')}
                                    disabled={!canEditInvoice || !item.canChangeCommission}
                                  />
                                </FormControl>,
                              ])}
                            />
                          </Grid>
                        </Grid>
                      </SimpleDialog>
                    }

                    <ConfirmationDialog
                      title={i18next.t('invoice-attendance-reuse-show-title')}
                      confirmButtonText={`${i18next.t('confirmation-dialog-yes')}`}
                      declineButtonText={`${i18next.t('confirmation-dialog-no')}`}
                      open={askSetShowForAttendance !== undefined}
                      setOpen={open => setTimeout(() => setAskSetShowForAttendance(open ? askSetShowForAttendance : undefined), 0)}
                      onConfirm={async () => {
                        getValues('days').forEach((d, dayIndex) => {
                          d.lineItems.forEach((li, lineItemIndex) => {
                            if (dayIndex === askSetShowForAttendance!.dayIndex && lineItemIndex === askSetShowForAttendance!.lineItemIndex) return;
                            if (li.hasAttendances) {
                              li.attendances.forEach((a, attendanceIndex) => {
                                if (a.profileId === askSetShowForAttendance!.profileId) {
                                  setValue(`days.${dayIndex}.lineItems.${lineItemIndex}.attendances.${attendanceIndex}.show`, false, {
                                    shouldDirty: true,
                                  });
                                }
                              });
                            }
                          });
                        });
                      }}
                    >
                      <Grid container spacing={2}>
                        <Grid item xs={12}>
                          {i18next.t('invoice-attendance-reuse-show-question')}
                        </Grid>
                      </Grid>
                    </ConfirmationDialog>
                    <Grid item xs={12}>
                      <Accordion defaultExpanded>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                          <Typography>
                            {canEdit && Object.keys(validationErrors).findIndex(v => v.startsWith('extraLineItems')) >= 0 ? <ErrorIcon /> : undefined}
                            {i18next.t('invoice-extras-tab')} ({activeExtraLineItemsCount})
                          </Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                          <Grid container spacing={2}>
                            <Grid item xs={12}>
                              <Typography variant="body2" color="text.secondary">
                                {i18next.t('invoice-extras-info')}
                              </Typography>
                            </Grid>
                            <Grid item xs={12}>
                              <Grid container spacing={1}>
                                {extraLineItemsFields.map((li, index) => (
                                  <InvoiceFormExtraLineItem
                                    key={index}
                                    extraLineItemIndex={index}
                                    disabled={!canEdit || isSubmitting}
                                    isPricesNet={version.priceList.isPricesNet}
                                    onEditInvoice={(canEditInvoice && !isSubmitting) ? () => launchEditInvoiceDialog() : undefined}
                                    extraLineItemRemove={index => {
                                      const lineItemId = getValues(`extraLineItems.${index}.lineItemId`);
                                      // Never persisted — drop from the form entirely
                                      if (!lineItemId) {
                                        removeExtraLineItemField(index);
                                      } else {
                                        setValue(`extraLineItems.${index}.isDeleted`, true);
                                      }
                                      onTotalsChanged();
                                      trigger('extraLineItems');
                                    }}
                                    {...props}
                                  />
                                ))}
                              </Grid>
                            </Grid>
                            {canEdit &&
                              extraLineItemsFields.map((li, index) => (
                                <InvoiceFormExtraLineItemListener
                                  key={`L_${index}`}
                                  extraLineItemIndex={index}
                                  extraLineItemChanged={extraLineItemChanged}
                                />
                              ))}
                            {canEdit && !isSubmitting && (
                              <Grid item xs={12}>
                                <Select
                                  value=""
                                  displayEmpty
                                  onChange={event => {
                                    if (!event.target.value) return;
                                    if (event.target.value === 'OTHER') {
                                      extraLineItemAddSpecialRequest();
                                      return;
                                    }
                                    extraLineItemAdd(`${event.target.value}`);
                                  }}
                                >
                                  <MenuItem value={''}>
                                    <em>{i18next.t('invoice-extras-selectsku')}</em>
                                  </MenuItem>
                                  <MenuItem value="OTHER">{i18next.t('offerblock-lineitem-other')}</MenuItem>
                                  <ListSubheader>{i18next.t('invoice-extras-selectsku-products')}</ListSubheader>
                                  {props.quickPriceCalculator.allProductsDb &&
                                    _.sortBy(props.quickPriceCalculator.allProductsDb, p =>
                                      `${quickPriceSkuDisplayName(
                                        props.productTexts,
                                        props.quickPriceCalculator.hotel?.id,
                                        p.sku,
                                        p.name,
                                        p.lookupCode,
                                        'product',
                                      )} ${p.sku}`.toLocaleLowerCase(),
                                    ).map((p, index) => (
                                      <MenuItem key={`P_${index}`} value={p.sku}>
                                        {quickPriceSkuDisplayName(
                                          props.productTexts,
                                          props.quickPriceCalculator.hotel?.id,
                                          p.sku,
                                          p.name,
                                          p.lookupCode,
                                          'product',
                                        )}
                                      </MenuItem>
                                    ))}
                                  <ListSubheader>{i18next.t('invoice-extras-selectsku-bundles')}</ListSubheader>
                                  {props.quickPriceCalculator.allBundlesDb &&
                                    _.sortBy(props.quickPriceCalculator.allBundlesDb, b =>
                                      `${quickPriceSkuDisplayName(
                                        props.productTexts,
                                        props.quickPriceCalculator.hotel?.id,
                                        b.sku,
                                        b.name,
                                        b.lookupCode,
                                        'bundle',
                                      )} ${b.sku}`.toLocaleLowerCase(),
                                    ).map((b, index) => (
                                      <MenuItem key={`B_${index}`} value={b.sku}>
                                        {quickPriceSkuDisplayName(
                                          props.productTexts,
                                          props.quickPriceCalculator.hotel?.id,
                                          b.sku,
                                          b.name,
                                          b.lookupCode,
                                          'bundle',
                                        )}
                                      </MenuItem>
                                    ))}
                                  <ListSubheader>{i18next.t('invoice-extras-selectsku-facilities')}</ListSubheader>
                                  {props.quickPriceCalculator.allFacilitiesDb &&
                                    _.sortBy(props.quickPriceCalculator.allFacilitiesDb, f =>
                                      `${quickPriceSkuDisplayName(
                                        props.productTexts,
                                        props.quickPriceCalculator.hotel?.id,
                                        f.sku,
                                        f.name,
                                        f.lookupCode,
                                        'facility',
                                        f,
                                      )} ${f.sku}`.toLocaleLowerCase(),
                                    ).map((f, index) => (
                                      <MenuItem key={`F_${index}`} value={f.sku}>
                                        {quickPriceSkuDisplayName(
                                          props.productTexts,
                                          props.quickPriceCalculator.hotel?.id,
                                          f.sku,
                                          f.name,
                                          f.lookupCode,
                                          'facility',
                                          f,
                                        )}
                                      </MenuItem>
                                    ))}
                                </Select>
                              </Grid>
                            )}
                          </Grid>
                        </AccordionDetails>
                      </Accordion>
                    </Grid>
                    {cancellationsFields.map((day, index) => (
                      <Grid item xs={12} key={index}>
                        <InvoiceFormCancellation key={index} ruleIndex={index} disabled={!canEdit || isSubmitting} {...props} />
                      </Grid>
                    ))}
                    {cancellationWaiverPreview && (
                      <Grid item xs={12}>
                        <PremiumInvoiceCancellationWaiverSection
                          preview={cancellationWaiverPreview}
                          currency={version.priceList.currency}
                          control={control}
                          disabled={!canEdit || isSubmitting}
                          waiveMode={(cancellationWaiveModeWatch || 'CHARGE') as CancellationWaiveMode}
                          amountToInvoice={
                            resolveCancellationAmountToInvoice({
                              mode: (cancellationWaiveModeWatch || 'CHARGE') as CancellationWaiveMode,
                              sum: cancellationWaiverPreview.sum,
                              chargedAmount: cancellationChargedAmountWatch,
                            })
                          }
                          onWaiveModeChange={mode => {
                            if (mode === 'PARTIAL') {
                              const current =
                                getValues('cancellationChargedAmount') ??
                                resolveCancellationAmountToInvoice({
                                  mode: 'CHARGE',
                                  sum: cancellationWaiverPreview.sum,
                                });
                              setValue('cancellationChargedAmount', Math.min(current, cancellationWaiverPreview.sum), {
                                shouldDirty: true,
                              });
                              syncCancellationAmountToInvoice(mode, Math.min(current, cancellationWaiverPreview.sum));
                            } else {
                              syncCancellationAmountToInvoice(mode);
                            }
                          }}
                        />
                      </Grid>
                    )}
                    {!cancellationWaiverPreview && cancellationsFields.length > 0 && (
                      <>
                        <Grid item xs={12} sm={2}>
                          <FormInputCurrency
                            name={'agreedCancellationWaiveNet'}
                            label={i18next.t('invoice-cancellation-agreedwaive')}
                            currency={version.priceList.currency}
                            control={control}
                            disabled
                          />
                        </Grid>
                        <Grid item xs={12} sm={2}>
                          <FormInputCurrency
                            name={'manualCancellationWaiveNet'}
                            label={i18next.t('invoice-cancellation-manualwaive')}
                            currency={version.priceList.currency}
                            control={control}
                            disabled={!canEdit || isSubmitting}
                          />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                          {canEdit && (
                            <Button
                              color="secondary"
                              variant="contained"
                              onClick={() => {
                                const { cancellationFeeNet } = calculateTotals({
                                  agreedCancellationWaiveNet: getValues('agreedCancellationWaiveNet'),
                                  manualCancellationWaiveNet: getValues('manualCancellationWaiveNet'),
                                  contentBlocks: [
                                    ...getValues('days').map(d => ({ type: EOfferVersionContentBlockType.LINE_ITEMS, lineItems: d.lineItems })),
                                    {
                                      type: EOfferVersionContentBlockType.LINE_ITEMS,
                                      lineItems: getValues('extraLineItems').filter(li => !li.isDeleted),
                                    },
                                    ...getValues('cancellations').map(d => ({
                                      type: EOfferVersionContentBlockType.CANCELLATION_ITEMS,
                                      lineItems: d.lineItems,
                                    })),
                                  ],
                                });
                                setValue('manualCancellationWaiveNet', cancellationFeeNet);
                              }}
                            >
                              {i18next.t('invoice-cancellation-full-waive-button')}
                            </Button>
                          )}
                        </Grid>
                      </>
                    )}
                  </Grid>,
                  <Grid container spacing={2}>
                    <Grid item sm={6}>
                      <Grid container spacing={2}>
                        <Grid item xs={12}>
                          <FormInputText
                            name="sender.businessName"
                            control={control}
                            label={i18next.t('invoice-sender-name')}
                            disabled={!canEdit || isSubmitting}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <FormInputText
                            name="sender.businessAddress1"
                            control={control}
                            label={i18next.t('invoice-sender-address1')}
                            disabled={!canEdit || isSubmitting}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <FormInputText
                            name="sender.businessAddress2"
                            control={control}
                            label={i18next.t('invoice-sender-address2')}
                            disabled={!canEdit || isSubmitting}
                          />
                        </Grid>
                        <Grid item xs={12} sm={3}>
                          <FormInputText
                            name="sender.businessZip"
                            control={control}
                            label={i18next.t('invoice-sender-zip')}
                            disabled={!canEdit}
                            required
                          />
                        </Grid>
                        <Grid item xs={12} sm={9}>
                          <FormInputText
                            name="sender.businessCity"
                            control={control}
                            label={i18next.t('invoice-sender-city')}
                            disabled={!canEdit}
                            required
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <FormInputCountryDropdown
                            name="sender.businessCountry"
                            control={control}
                            label={i18next.t('invoice-sender-country')}
                            disabled={!canEdit || isSubmitting}
                            required
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <FormInputText
                            name="sender.billingUid"
                            control={control}
                            label={i18next.t('invoice-sender-billing-uid')}
                            disabled={!canEdit || isSubmitting}
                            required
                          />
                        </Grid>
                      </Grid>
                    </Grid>
                    <Grid item sm={6}>
                      <Grid container spacing={2}>
                        <Grid item xs={12}>
                          <FormInputText
                            name="sender.businessEmail"
                            control={control}
                            label={i18next.t('invoice-sender-email')}
                            disabled={!canEdit || isSubmitting}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <FormInputText
                            name="sender.businessPhone"
                            control={control}
                            label={i18next.t('invoice-sender-phone')}
                            disabled={!canEdit || isSubmitting}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <FormInputText
                            name="sender.contactName"
                            control={control}
                            label={i18next.t('invoice-sender-contact')}
                            disabled={!canEdit || isSubmitting}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <FormInputText
                            name="sender.comment"
                            control={control}
                            label={i18next.t('invoice-sender-comment')}
                            disabled={!canEdit || isSubmitting}
                          />
                        </Grid>
                      </Grid>
                    </Grid>
                    {canEdit && canApprove && (
                      <Grid item xs={12} justifyItems={'end'}>
                        <FormControl>
                          <FormControlLabel
                            control={
                              <Controller
                                name="sender.accepted"
                                control={control}
                                render={({ field: { onChange, value } }) => (
                                  <Checkbox color="primary" checked={!!value} onChange={onChange} />
                                )}
                              />
                            }
                            label={commissionConfirmationLabel}
                          />
                        </FormControl>
                      </Grid>
                    )}
                    {canSEMApprove && (
                      <>
                        <Grid item xs={12}>
                          <hr />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                          <Grid container spacing={2}>
                            <Grid item xs={12}>
                              <FormInputDropdown
                                name="commissionBase"
                                control={control}
                                label={i18next.t('invoice-commission-base')}
                                disabled={!canEdit || isSubmitting}
                                options={Object.values(EOfferCommissionBase).map(cb => ({
                                  label: i18next.t(`enums-EOfferCommissionBase-${cb}`),
                                  value: cb,
                                }))}
                                required
                              />
                            </Grid>
                            <Grid item xs={12}>
                              <FormInputCurrency
                                name="commissionBaseAmount"
                                control={control}
                                label={i18next.t('invoice-commission-base-amount')}
                                disabled={!canEdit || isSubmitting}
                                currency={version.priceList.currency}
                                helperText={
                                  commissionBaseWatch === EOfferCommissionBase.NET
                                    ? i18next.t('invoice-commission-base-amount-net')
                                    : i18next.t('invoice-commission-base-amount-gross')
                                }
                                required
                              />
                            </Grid>
                            <Grid item xs={12}>
                              <FormInputPercentage
                                name="commissionPercentage"
                                control={control}
                                label={i18next.t('invoice-commission-percentage')}
                                disabled={!canEdit || isSubmitting}
                                required
                              />
                            </Grid>
                          </Grid>
                        </Grid>
                        <Grid item xs={12}>
                          <FormControl>
                            <FormControlLabel
                              control={
                                <Controller
                                  name="commissionAccepted"
                                  control={control}
                                  render={({ field: { onChange, value } }) => (
                                    <Checkbox color="primary" checked={!!value} onChange={onChange} required />
                                  )}
                                />
                              }
                              label={semCommissionInfoLabel}
                            />
                          </FormControl>
                        </Grid>
                      </>
                    )}
                  </Grid>,
                  <Grid container spacing={2}>
                    <Grid item xs={3}>
                      <Grid container spacing={1}>
                        {generatedInvoices &&
                          generatedInvoices
                            .filter(gci => gci.type === EOfferVersionType.CLIENTINVOICE)
                            .map((gci, i) => (
                              <Grid item xs={12} key={i}>
                                <Button
                                  color="secondary"
                                  variant="contained"
                                  disabled={showGeneratedInvoiceId === gci.id}
                                  onClick={() => setShowGeneratedInvoiceId(gci.id)}
                                >
                                  {gci.title || `Beleg ${i + 1}`}
                                </Button>
                              </Grid>
                            ))}
                        {generatedInvoices &&
                          generatedInvoices
                            .filter(gci => gci.type === EOfferVersionType.COSTCENTRE)
                            .map((gci, i) => (
                              <Grid item xs={12} key={i}>
                                <Button
                                  color="secondary"
                                  variant="contained"
                                  disabled={showGeneratedInvoiceId === gci.id}
                                  onClick={() => setShowGeneratedInvoiceId(gci.id)}
                                >
                                  {gci.title || `Kostenstellen-Beleg ${i + 1}`}
                                </Button>
                              </Grid>
                            ))}
                      </Grid>
                    </Grid>
                    <Grid item xs={9}>
                      {showGeneratedInvoiceId && (
                        <iframe src={getFullBackendUrl(`/backend/html/showOffer/${showGeneratedInvoiceId}`)} width="100%" height="900"></iframe>
                      )}
                    </Grid>
                  </Grid>,
                  <BookingTasksList bookingId={props.offer.bookingId ?? props.offer.id} bookingRefCode={props.offer.refCode} bookingTitle={props.offer.bookingTitle ?? undefined} publicClientId={props.version.offer?.publicClient?.id} publicCompanyId={props.offer.publicCompany?.id} />,
                  <Grid container>
                    <Grid item xs={12}>
                      <OfferHistoryTable history={props.offer.history} emailHistory={props.offer.emailHistory} canSeeClientData={true} />
                    </Grid>
                  </Grid>,
                ]}
              />
            </FormProvider>
          </ContactPersonTabsWrapper>
        </Grid>
        <Grid item xs={12}>
          <hr />
        </Grid>
        <Grid item xs={12}>
          <Typography variant="h6">{i18next.t('invoice-totals')}</Typography>
        </Grid>
        <Grid item xs={12}>
          {i18next.t('invoice-totals-net')}: {formatPrice(watch('totalPriceNet'), version.priceList.currency)}
        </Grid>
        <Grid item xs={12}>
          {i18next.t('invoice-totals-taxes')}: {formatPrice(watch('totalPriceGross') - watch('totalPriceNet'), version.priceList.currency)}
        </Grid>
        <Grid item xs={12}>
          {i18next.t('invoice-totals-gross')}: {formatPrice(watch('totalPriceGross'), version.priceList.currency)}
        </Grid>
      </Grid>
    </>
  );
}

export default function InvoicePremiumForm(props: InvoicePremiumFormProps) {
  const user = userSelector()!;

  const [stepId, setStepId] = useState(0);

  if (props.version.status === EOfferVersionStatus.CIV_WAITFORHOTEL)
    return <InvoiceCancellationFormHotel key={stepId} {...props} onProcessStep={() => setStepId(stepId + 1)} />;
  else
    return <InvoicePremiumFormEdit key={stepId} {...props} />;
}
